import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { casesApi, dashboardApi, emergencySafetyApi, eventsApi, homeCareApi, householdGraphApi, importCandidatesApi, moneyApi, petsApi, propertyProfileApi } from "../services/api";
import ConfirmModal from "../components/shared/ConfirmModal";
import { useToastActions } from "../components/shared/Toast";
import { useAuth } from "../hooks/useAuth";

// ─── Priority config ──────────────────────────────────────────────────────────

const PRIORITY_META = {
  urgent:   { label: "Urgent",   color: "#b91c1c",  bg: "rgba(185,28,28,0.10)",   border: "rgba(185,28,28,0.28)"   },
  soon:     { label: "Soon",     color: "#b45309",  bg: "rgba(180,83,9,0.10)",    border: "rgba(180,83,9,0.28)"    },
  upcoming: { label: "Upcoming", color: "#64748b",  bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.20)" },
  fyi:      { label: "FYI",      color: "#64748b",  bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.20)" },
  savings:  { label: "Save $",   color: "#047857",  bg: "rgba(4,120,87,0.10)",    border: "rgba(4,120,87,0.24)"    },
};

const PRIORITY_RANK = {
  urgent: 1,
  soon: 2,
  savings: 3,
  upcoming: 4,
  fyi: 4,
};

const DAILY_BRIEF_OVERLAY_QUERY = "(max-width: 768px), (min-width: 769px) and (max-width: 1024px) and (orientation: portrait)";

const weatherConditionLabel = (code) => {
  const value = Number(code);
  if (value === 0) return "clear";
  if ([1, 2].includes(value)) return "mostly clear";
  if (value === 3) return "overcast";
  if ([45, 48].includes(value)) return "foggy";
  if (value >= 51 && value <= 57) return "drizzly";
  if (value >= 61 && value <= 67) return "rainy";
  if (value >= 71 && value <= 77) return "snowy";
  if (value >= 80 && value <= 82) return "showery";
  if (value >= 85 && value <= 86) return "snowy";
  if (value >= 95) return "stormy";
  return "settled";
};

const MODULE_BADGE = {
  food:     { label: "Food",    color: "#ff6b6b" },
  gifts:    { label: "Gifts",   color: "#ff4d4d" },
  vault:    { label: "Vault",   color: "#9aa7b8" },
  vehicles: { label: "Vehicles",color: "#f5a623" },
  subs:     { label: "Subs",    color: "#47d7ff" },
  trips:    { label: "Trips",   color: "#c084fc" },
  events:   { label: "Events",  color: "#818cf8" },
  utilities:{ label: "Utilities", color: "#47d7ff" },
  sharing:  { label: "Family",  color: "#2a7b6f" },
  property: { label: "Property", color: "#2a7b6f" },
  pets:     { label: "Pets", color: "#d977a8" },
  cases:    { label: "Cases", color: "#b91c1c" },
  discoveries: { label: "Review", color: "#7c3aed" },
  predictions: { label: "Forecast", color: "#2563eb" },
  household:{ label: "Household", color: "#64748b" },
};

const ATTENTION_ICON = {
  food: "🥬",
  gifts: "🎁",
  vault: "🏠",
  vehicles: "🚗",
  subs: "🔁",
  trips: "✈",
  events: "📅",
  utilities: "💡",
  sharing: "👥",
};

const PANTRY_CATEGORY_ICON = {
  produce: "🥦",
  dairy: "🥛",
  meat: "🥩",
  seafood: "🐟",
  frozen: "🧊",
  fridge: "🧊",
  pantry: "🫙",
  beverages: "🧃",
  snacks: "🍿",
  bakery: "🍞",
  other: "📦",
};

const EVENT_TYPE_LABELS = {
  trip: "Trip",
  concert: "Concert",
  wedding: "Wedding",
  birthday_party: "Birthday party",
  graduation_party: "Graduation party",
  holiday_gathering: "Holiday gathering",
  game_night: "Game night",
  anniversary_dinner: "Anniversary",
  baby_shower: "Baby shower",
  other_event: "Event",
};

function titleCase(value = "") {
  return String(value || "")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function eventTypeLabel(value) {
  return EVENT_TYPE_LABELS[value] || titleCase(value || "Event");
}

function eventDisplayTitle(event = {}) {
  if (event.title) return event.title;
  const type = event.occasion_type || event.event_type;
  const destination = event.destination || event.notes?.match(/Destination: ([^|]+)/)?.[1]?.trim();
  const style = event.trip_style || event.notes?.match(/Style: ([^|]+)/)?.[1]?.trim();
  if (type === "trip" || destination || style) {
    if (destination && style) return `${destination} ${String(style).toLowerCase()} trip`;
    if (destination) return `${destination} trip`;
    return "Trip";
  }
  const person = event.person_name;
  const label = eventTypeLabel(type);
  if (person && !/myself/i.test(person)) return `${person}'s ${label.toLowerCase()}`;
  return label;
}

function eventSubtitle(event = {}) {
  const destination = event.destination || event.location;
  if (destination) return destination;
  return eventTypeLabel(event.occasion_type || event.event_type);
}

function pantryAttentionIcon(item = {}) {
  const name = String(item.pantry_name || item.title || "").replace(/\s+expiring$/i, "").toLowerCase();
  if (/egg/.test(name)) return "🥚";
  if (/milk powder|formula|nido/.test(name)) return "🍼";
  if (/(^|\s)milk(\s|$)/.test(name)) return "🥛";
  if (/butter/.test(name)) return "🧈";
  if (/cheese/.test(name)) return "🧀";
  if (/yogurt|yoghurt/.test(name)) return "🫙";
  if (/cream/.test(name)) return "🥛";
  if (/chicken/.test(name)) return "🍗";
  if (/beef|steak/.test(name)) return "🥩";
  if (/fish|salmon|tuna|shrimp|tilapia/.test(name)) return "🐟";
  if (/banana/.test(name)) return "🍌";
  if (/apple/.test(name)) return "🍎";
  if (/tomato/.test(name)) return "🍅";
  if (/potato/.test(name)) return "🥔";
  if (/onion|garlic/.test(name)) return "🧅";
  if (/spinach|lettuce|kale|greens/.test(name)) return "🥬";
  if (/bread|bagel|tortilla/.test(name)) return "🍞";
  if (/rice|pasta|noodle/.test(name)) return "🍚";
  if (/water|juice|soda|tea|coffee/.test(name)) return "🧃";
  return PANTRY_CATEGORY_ICON[item.pantry_category] || "🥬";
}

function attentionIcon(item = {}) {
  if (item.isInsight) return "✦";
  if (item.item_type === "trip_plan_needed") return "✈";
  if (item.item_type === "meal_plan_ending") return "🍽";
  if (item.item_type === "pantry_expiry") return pantryAttentionIcon(item);
  if (item.item_type === "occasion_upcoming") return "🎁";
  if (item.item_type?.includes("filter") || item.item_type?.includes("maintenance")) return "🏠";
  if (item.item_type?.includes("warranty")) return "🛡";
  if (item.item_type?.includes("bill")) return "💡";
  if (item.item_type?.includes("renewal") || item.item_type?.includes("trial")) return "🔁";
  return ATTENTION_ICON[item.source_module] || "◇";
}

function getPriorityRank(priority) {
  return PRIORITY_RANK[priority] || PRIORITY_RANK.fyi;
}

function sortByPriority(items = []) {
  return [...items].sort((a, b) => {
    const rankDiff = getPriorityRank(a.priority) - getPriorityRank(b.priority);
    if (rankDiff !== 0) return rankDiff;
    const aDate = new Date(a.due_date || a.created_at || "9999-12-31").getTime();
    const bDate = new Date(b.due_date || b.created_at || "9999-12-31").getTime();
    return aDate - bDate;
  });
}

function attentionDescription(item = {}) {
  const desc = String(item.description || "").trim();
  if (!desc) return "";
  if (item.item_type === "pantry_expiry" && /^Expires in \d+ days?\.$/i.test(desc)) {
    return desc.replace(/\.$/, " — plan a meal around it.");
  }
  if (item.item_type === "bill_due" && /^Due (?:today|in \d+ days?)\.$/i.test(desc)) {
    return desc.replace(/\.$/, " — review the bill and confirm payment.");
  }
  return desc;
}

const ROUTE_ALIASES = {
  "/food/expiring": "/app/food",
  "/gifts": "/app/people",
  "/home-vault/add": "/app/home-vault",
  "/trips": "/app/events",
};

const briefingSessionKey = () => {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `housemind:daily-briefing-dismissed:${today}`;
};

const wasBriefingDismissedToday = () => {
  try {
    return window.localStorage.getItem(briefingSessionKey()) === "1";
  } catch {
    return false;
  }
};

const setBriefingDismissedToday = (dismissed) => {
  try {
    const key = briefingSessionKey();
    if (dismissed) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
  } catch {}
};

function localISODate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function daysUntilLocalDate(value) {
  if (!value) return null;
  const due = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function toAppRoute(target) {
  if (!target) return "/app";
  if (/^(https?:|mailto:)/i.test(target)) return target;
  if (target.startsWith("/app")) return target;

  const [path, suffix = ""] = target.split(/(?=[?#])/, 2);
  const normalizedPath = (path || "/").replace(/\/+$/, "") || "/";
  const alias = ROUTE_ALIASES[normalizedPath];
  if (alias) return `${alias}${suffix}`;

  return `/app${target.startsWith("/") ? target : `/${target}`}`;
}

function navigateToApp(navigate, target) {
  const route = toAppRoute(target);
  if (/^https?:/i.test(route)) {
    window.location.assign(route);
    return;
  }
  navigate(route);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  const meta = PRIORITY_META[priority] || PRIORITY_META.fyi;
  return (
    <span style={{
      display: "inline-block",
      fontSize: 10,
      fontWeight: 700,
      padding: "2px 7px",
      borderRadius: 20,
      color: meta.color,
      background: meta.bg,
      border: `1px solid ${meta.border}`,
      letterSpacing: "0.03em",
      flexShrink: 0,
    }}>
      {meta.label}
    </span>
  );
}

function useHorizontalCardSwipe(onSwipe) {
  const gestureRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [offset, setOffset] = useState(0);

  const reset = useCallback(() => {
    gestureRef.current = null;
    setOffset(0);
  }, []);

  const onPointerDown = useCallback(event => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    gestureRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, horizontal: null };
  }, []);

  const onPointerMove = useCallback(event => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (gesture.horizontal === null && (Math.abs(dx) > 7 || Math.abs(dy) > 7)) {
      gesture.horizontal = Math.abs(dx) > Math.abs(dy) * 1.2;
    }
    if (!gesture.horizontal) return;
    event.preventDefault();
    setOffset(Math.max(-96, Math.min(96, dx)));
  }, []);

  const onPointerEnd = useCallback(event => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const dx = event.clientX - gesture.x;
    const shouldSwipe = gesture.horizontal && Math.abs(dx) >= 64;
    gestureRef.current = null;
    if (shouldSwipe) {
      suppressClickRef.current = true;
      setOffset(dx < 0 ? -112 : 112);
      window.setTimeout(() => {
        setOffset(0);
        onSwipe();
      }, 120);
      return;
    }
    setOffset(0);
  }, [onSwipe]);

  const consumeClick = useCallback(event => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
    return true;
  }, []);

  return {
    offset,
    consumeClick,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: reset,
    },
  };
}

function AttentionCard({ item, onDismiss }) {
  const navigate = useNavigate();
  const description = attentionDescription(item);
  const fullActionLabel = String(item.action_label || "").trim();
  const openItem = () => {
    if (item.action_url) navigateToApp(navigate, item.action_url);
  };
  const swipe = useHorizontalCardSwipe(() => onDismiss(item));
  return (
    <div
      className={`hm-attention-card is-${item.priority || "fyi"}${item.isInsight ? " is-insight" : ""}${item.action_url ? " is-clickable" : ""}`}
      role={item.action_url ? "link" : undefined}
      tabIndex={item.action_url ? 0 : undefined}
      aria-label={item.action_url ? (fullActionLabel || `Open ${item.title}`) : undefined}
      style={{ "--hm-card-swipe-x": `${swipe.offset}px` }}
      {...swipe.handlers}
      onClick={event => { if (!swipe.consumeClick(event)) openItem(); }}
      onKeyDown={event => {
        if (!item.action_url || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        openItem();
      }}
    >
      <div className="hm-attention-icon" aria-hidden="true">{attentionIcon(item)}</div>
      <div className="hm-attention-main">
        <div className="hm-attention-title-row">
          <span className="hm-attention-title">{item.title}</span>
          <PriorityBadge priority={item.priority} />
        </div>
        {description && <p className="hm-attention-description">{description}</p>}
      </div>
      <div className="hm-attention-actions">
        {item.action_url && <span className="hm-card-open-chevron" aria-hidden="true">›</span>}
      </div>
    </div>
  );
}

function BulkAttentionModal({ group, selectedKeys, busy, error, onToggle, onToggleAll, onClose, onApply }) {
  if (!group) return null;
  const selectedCount = group.items.filter(item => selectedKeys.has(`${item.source_module}-${item.item_id}-${item.item_type}-${item.due_date || ""}`)).length;
  return (
    <div className="modal-shell" onClick={busy ? undefined : onClose}>
      <div className="card modal-card hm-attention-bulk-modal" onClick={event => event.stopPropagation()}>
        <div className="flex-between mb-16">
          <div><small className="hm-modal-eyebrow">Bulk action</small><h3>{group.label}</h3></div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>x</button>
        </div>
        <button type="button" className="hm-attention-select-all" onClick={onToggleAll} disabled={busy}>
          {selectedCount === group.items.length ? "Clear all" : "Select all"} <span>{selectedCount}/{group.items.length}</span>
        </button>
        <div className="hm-attention-bulk-list">
          {group.items.map(item => {
            const key = `${item.source_module}-${item.item_id}-${item.item_type}-${item.due_date || ""}`;
            return <label key={key}><input type="checkbox" checked={selectedKeys.has(key)} onChange={() => onToggle(key)} disabled={busy} /><span><strong>{item.title}</strong><small>{attentionDescription(item)}</small></span></label>;
          })}
        </div>
        {error && <p className="form-error">{error}</p>}
        <div className="hm-attention-bulk-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-secondary" onClick={() => onApply("remind_tomorrow")} disabled={busy || !selectedCount}>Remind tomorrow</button>
          <button className="btn btn-primary" onClick={() => onApply("done")} disabled={busy || !selectedCount}>{busy ? "Saving…" : "Done"}</button>
        </div>
      </div>
    </div>
  );
}

function MorningBriefSection({ icon, title, items = [], emptyText }) {
  const visible = items.slice(0, 3);
  return (
    <section className="hm-morning-section">
      <div className="hm-morning-section__head">
        <strong><span aria-hidden="true">{icon}</span>{title}</strong>
        {items.length > 0 && <em>{items.length}</em>}
      </div>
      {visible.length ? (
        <div className="hm-morning-list">
          {visible.map((item, index) => (
            <div className="hm-morning-item" key={item.id || `${item.title}-${index}`}>
              <span className="hm-morning-item__icon" aria-hidden="true">{item.icon || attentionIcon(item)}</span>
              <div className="hm-morning-item__copy">
                {item.eyebrow && <em>{item.eyebrow}</em>}
                <strong>{item.title}</strong>
                {item.detail && <small>{item.detail}</small>}
              </div>
            </div>
          ))}
        </div>
      ) : <p className="hm-morning-section__empty">{emptyText}</p>}
      {items.length > 3 && <small className="hm-morning-section__more">+{items.length - 3} more in Action Needed</small>}
    </section>
  );
}

function TodayTasksPanel({
  tasks = [],
  draft,
  onDraftChange,
  onAdd,
  onComplete,
  onMoveTomorrow,
  onDelete,
  busyId,
  showAll,
  onToggleShowAll,
  open,
  onToggleOpen,
  decisionTask,
  onDecisionTaskChange,
}) {
  const openTasks = tasks.filter(task => task.status !== "completed");
  const visibleOpenTasks = showAll ? openTasks : openTasks.slice(0, 3);
  const hiddenCount = Math.max(0, openTasks.length - visibleOpenTasks.length);

  return (
    <div className={`hm-today-tasks${open ? " is-open" : " is-collapsed"}`}>
      <button type="button" className="hm-today-tasks__head" onClick={onToggleOpen} aria-expanded={open}>
        <div>
          <span>Today</span>
          <strong>My tasks</strong>
        </div>
        <div className="hm-today-tasks__summary">
          <em>{openTasks.length ? `${openTasks.length} open` : "All clear"}</em>
          <span aria-hidden="true">{open ? "-" : "+"}</span>
        </div>
      </button>

      {open && (
        <>
          <form className="hm-today-tasks__add" onSubmit={onAdd}>
            <input
              value={draft}
              onChange={event => onDraftChange(event.target.value)}
              placeholder="Add a quick task for today..."
              maxLength={255}
            />
            <button type="submit" disabled={!draft.trim() || busyId === "add"} aria-label="Add today task">
              +
            </button>
          </form>

          {visibleOpenTasks.length > 0 && (
            <div className="hm-today-tasks__list">
              {visibleOpenTasks.map(task => (
                <div className="hm-today-task-wrap" key={task.id}>
                  <div className="hm-today-task">
                    <button
                      type="button"
                      className="hm-today-task__check"
                      onClick={() => onDecisionTaskChange(decisionTask?.id === task.id ? null : task)}
                      disabled={busyId === task.id}
                      aria-label={`Choose what to do with ${task.title}`}
                    />
                    <span>{task.title}</span>
                    <button
                      type="button"
                      className="hm-today-task__delete"
                      onClick={() => onDelete(task)}
                      disabled={busyId === task.id}
                      title="Remove task"
                      aria-label={`Remove ${task.title}`}
                    >
                      x
                    </button>
                  </div>
                  {decisionTask?.id === task.id && (
                    <div className="hm-today-task-decision">
                      <span>What should happen?</span>
                      <button type="button" onClick={() => onComplete(task)} disabled={busyId === task.id}>
                        Mark done
                      </button>
                      <button type="button" onClick={() => onMoveTomorrow(task)} disabled={busyId === task.id}>
                        Remind tomorrow
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {openTasks.length === 0 && (
            <div className="hm-today-tasks__empty">Nothing personal added for today.</div>
          )}

          {openTasks.length > 3 && (
            <button type="button" className="hm-today-tasks__toggle" onClick={onToggleShowAll}>
              {showAll ? "Show fewer" : `Show ${hiddenCount} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function AttentionDismissModal({ item, onClose, onDone, onRemind }) {
  if (!item) return null;
  return (
    <div className="modal-shell" onClick={onClose}>
      <div className="card modal-card" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="flex-between mb-16">
          <h3 style={{ fontSize: 20 }}>Dismiss this item?</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>x</button>
        </div>
        <div className="notice-banner mb-16">
          <p><strong>{item.title}</strong></p>
          {item.description && <p>{item.description}</p>}
        </div>
        <div className="flex-center gap-8" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={() => onRemind(item)}>Remind tomorrow</button>
          <button className="btn btn-primary" onClick={() => onDone(item)}>Done</button>
        </div>
      </div>
    </div>
  );
}

function WidgetCard({ title, children, action, onAction, loading }) {
  return (
    <div className="hm-dashboard-widget-card" style={{
      background: "var(--hm-card)",
      border: "1px solid var(--hm-line)",
      borderRadius: 18,
      padding: "18px 18px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--hm-text)", letterSpacing: "-0.01em" }}>
          {title}
        </span>
        {action && (
          <button
            onClick={onAction}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--hm-cyan)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "2px 0",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            {action} →
          </button>
        )}
      </div>
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--hm-muted)", fontSize: 13 }}>
          <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Loading…
        </div>
      ) : (
        <div style={{ flex: 1 }}>{children}</div>
      )}
    </div>
  );
}

function WidgetStat({ value, label, color }) {
  return (
    <div className="hm-dashboard-widget-stat" style={{ textAlign: "center", padding: "4px 0" }}>
      <div className="hm-dashboard-widget-stat__value" style={{ fontSize: 28, fontWeight: 800, color: color || "var(--hm-cyan)", lineHeight: 1 }}>{value}</div>
      <div className="hm-dashboard-widget-stat__label" style={{ fontSize: 12, color: "var(--hm-muted)", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function TodayMealsGrid({ meals = [] }) {
  const mealSlots = ["breakfast", "lunch", "dinner"];
  const mealByType = meals.reduce((acc, meal) => {
    const type = String(meal?.meal_type || "").toLowerCase();
    if (type) acc[type] = meal;
    return acc;
  }, {});
  const mealLabel = (meal) => meal?.display_meal_name || meal?.meal_name || "Not planned";
  const mealTypeLabel = (type) => type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <div className="hm-today-meals">
      <div className="hm-today-meals__title">Today&apos;s meals</div>
      <div className="hm-today-meals__grid">
        {mealSlots.map(type => {
          const meal = mealByType[type];
          return (
            <div className={`hm-today-meals__card${meal ? "" : " is-empty"}`} key={type}>
              <span>{mealTypeLabel(type)}</span>
              <strong>{mealLabel(meal)}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const IMPORT_MODULE_META = {
  subscriptions: { label: "Subscriptions", icon: "🔁" },
  utilities: { label: "Utilities", icon: "💡" },
  vault: { label: "Home Vault", icon: "🏠" },
  pantry: { label: "Food & Pantry", icon: "🥬" },
  vehicles: { label: "Vehicles", icon: "🚗" },
  events: { label: "Events", icon: "📅" },
  people: { label: "People & Gifts", icon: "🎁" },
  pets: { label: "Pets", icon: "🐾" },
  safety: { label: "Emergency & Safety", icon: "🧯" },
  property_profile: { label: "Property Profile", icon: "🏡" },
};
const SUPPORTED_IMPORT_MODULES = new Set(["subscriptions", "utilities", "vault", "pantry", "vehicles", "events", "pets", "safety", "property_profile"]);
const MERGE_SUPPORTED_MODULES = new Set(["subscriptions", "utilities", "vault", "pantry", "vehicles", "events", "pets", "safety"]);
const AUTO_UPDATE_TRUST_MODULES = new Set(["subscriptions", "utilities", "pantry", "vault", "vehicles", "events"]);
const IMPORT_RECORD_ROUTES = {
  Subscription: "/app/subscriptions",
  UtilityBill: "/app/utilities",
  VaultItem: "/app/home-vault",
  PantryItem: "/app/food",
  Vehicle: "/app/vehicles",
  PersonalEvent: "/app/events",
  PetCareSchedule: "/app/pets",
  SafetyResource: "/app/emergency-safety",
  PropertyProfile: "/app/property-profile",
};

function confidenceMeta(score = 0) {
  const value = Number(score || 0);
  if (value >= 0.9) return { label: "Ready to import", tone: "high" };
  if (value >= 0.7) return { label: "Review fields", tone: "medium" };
  return { label: "Needs verification", tone: "low" };
}

function candidateReviewMeta(candidate = {}) {
  return candidate.review_meta || {};
}

function candidateIsPossibleDuplicate(candidate = {}) {
  return ["possible_duplicate", "suggested_update", "conflict"].includes(candidateReviewMeta(candidate).match_status);
}

const MERGE_TRUST_NOTES = {
  subscriptions: "Future statements from this provider may auto-update this record (amount, renewal date, billing cycle) once they clear HouseMind's safety checks. Large or unusual changes, or a cancellation, always come back to you. Every automatic update is logged with an Undo option.",
  utilities: "Future statements from this provider may auto-update this record (amount, due date, autopay) once they clear HouseMind's safety checks. Large or unusual changes, a new account number, or a cancellation always come back to you. Every automatic update is logged with an Undo option.",
  pantry: "Future statements from this provider may auto-update this item's quantity and expiration once they clear HouseMind's safety checks. Every automatic update is logged with an Undo option.",
  vault: "Future statements from this provider may auto-update this item's warranty date and purchase price once they clear HouseMind's safety checks. Unusual price changes or stale dates always come back to you. Every automatic update is logged with an Undo option.",
  vehicles: "Future statements from this provider may auto-update this vehicle's mileage once they clear HouseMind's safety checks. Mileage never moves backward automatically. Every automatic update is logged with an Undo option.",
  events: "Future statements from this provider may auto-update this event's end date once they clear HouseMind's safety checks. Every automatic update is logged with an Undo option.",
};

function mergeTrustNote(module) {
  return MERGE_TRUST_NOTES[module] || "Future statements from this provider may auto-update this record once they clear HouseMind's safety checks. Every automatic update is logged with an Undo option.";
}

function candidateWhyLines(candidate = {}) {
  const reviewMeta = candidateReviewMeta(candidate);
  const lines = Array.isArray(reviewMeta.why_lines) ? reviewMeta.why_lines.filter(Boolean) : [];
  if (lines.length) return lines.slice(0, 5);
  const fallback = [];
  if (reviewMeta.source_label) fallback.push(`Source: ${reviewMeta.source_label}.`);
  if (candidate.raw_subject) fallback.push(`Subject: ${candidate.raw_subject}.`);
  if (reviewMeta.detection_reason) fallback.push(`Detected because ${reviewMeta.detection_reason}.`);
  if (reviewMeta.match_label) fallback.push(reviewMeta.match_label);
  return fallback.slice(0, 4);
}

function dismissalReasonLabel(reason = "") {
  const labels = {
    duplicate: "Dismissed as duplicate",
    not_relevant: "Dismissed as not relevant",
    wrong_details: "Dismissed for wrong details",
    user_dismissed: "Dismissed",
  };
  return labels[reason] || "";
}

function candidateTitle(candidate = {}) {
  const payload = candidate.proposed_payload || {};
  if (payload.provider) return payload.provider;
  if (payload.title) return payload.title;
  if (payload.name) return payload.name;
  if (payload.item_name) return payload.item_name;
  if (payload.make || payload.model) return `${payload.year || ""} ${payload.make || ""} ${payload.model || ""}`.trim();
  return candidate.raw_subject || titleCase(candidate.detected_type || "Import");
}

function candidateFields(candidate = {}) {
  const payload = candidate.proposed_payload || {};
  const fieldMap = [
    ["provider", "Provider"],
    ["amount", "Amount"],
    ["billing_cycle", "Cycle"],
    ["next_renewal_date", "Renews"],
    ["service_type", "Service"],
    ["due_date", "Due"],
    ["retailer", "Retailer"],
    ["purchase_date", "Purchased"],
    ["warranty_expiry", "Warranty"],
    ["model_number", "Model"],
    ["quantity", "Qty"],
    ["estimated_expiry", "Expires"],
    ["start_date", "Starts"],
    ["end_date", "Ends"],
    ["destination", "Destination"],
    ["year", "Year"],
    ["make", "Make"],
    ["model", "Model"],
  ];
  return fieldMap
    .map(([key, label]) => [label, payload[key]])
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .slice(0, 6);
}

function groupCandidates(candidates = []) {
  return candidates.reduce((groups, candidate) => {
    const key = candidate.proposed_module || "other";
    if (!groups[key]) groups[key] = [];
    groups[key].push(candidate);
    return groups;
  }, {});
}

function filterOptionsFromCounts(counts = {}, fallback = []) {
  const keys = Array.from(new Set([...fallback, ...Object.keys(counts || {})].filter(Boolean)));
  return keys.map(value => ({ value, count: counts?.[value] || 0 }));
}

function routeForImportedCandidate(candidate = {}) {
  return IMPORT_RECORD_ROUTES[candidate.imported_record_type] || null;
}

function importSavedMessage(candidate, result, verb = "imported to") {
  const record = result?.updated_record || result?.created_record || {};
  const moduleLabel = IMPORT_MODULE_META[candidate.proposed_module]?.label || "HouseMind";
  if (candidate.proposed_module === "utilities" && record.amount != null) {
    const due = record.due_date ? `, due ${formatShortDate(record.due_date)}` : "";
    return `${record.provider || candidateTitle(candidate)} ${moneyValue(record.amount)} bill saved to Utilities history${due}.`;
  }
  if (candidate.proposed_module === "subscriptions" && record.amount != null) {
    return `${record.provider || record.name || candidateTitle(candidate)} updated to ${moneyValue(record.amount)} in Subscriptions.`;
  }
  return `${candidateTitle(candidate)} ${verb} ${moduleLabel}.`;
}

function moneyValue(value) {
  return Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatShortDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function syncStatusMeta(source = {}) {
  const status = source.last_sync_status || "";
  if (status === "success") return { label: "Sync healthy", tone: "success" };
  if (status === "error") return { label: "Sync needs attention", tone: "error" };
  if (status === "skipped") return { label: "Sync skipped", tone: "skipped" };
  if (source.permission_status === "connected") return { label: "Not synced yet", tone: "pending" };
  return null;
}

function connectionAttentionReason(source = {}, now = Date.now()) {
  if (source.status === "disabled") return null;
  if (source.permission_status === "pending_user_setup") return "Setup was not completed";
  if (source.permission_status !== "connected") return null;
  if (source.last_sync_status === "error") return source.last_sync_error || "The latest scan failed";
  if (!source.last_synced_at) return "Connected, but the first scan has not completed";
  const lastSync = new Date(source.last_synced_at).getTime();
  if (Number.isFinite(lastSync) && now - lastSync > 36 * 60 * 60 * 1000) return "No successful scan in the last 36 hours";
  return null;
}

function syncCursorLabel(cursor = {}) {
  const emailCount = Array.isArray(cursor.checked_message_ids) ? cursor.checked_message_ids.length : 0;
  const eventCount = Array.isArray(cursor.checked_event_ids) ? cursor.checked_event_ids.length : 0;
  if (emailCount) return `${emailCount} email${emailCount === 1 ? "" : "s"} checked`;
  if (eventCount) return `${eventCount} event${eventCount === 1 ? "" : "s"} checked`;
  return "";
}

function syncRunSummary(cursor = {}) {
  const run = cursor.last_run || {};
  if (run.checked == null) return [];
  const reasons = run.reason_counts || {};
  return [
    `${Number(run.checked || 0)} examined`,
    `${Number(run.created || 0)} new review item${Number(run.created || 0) === 1 ? "" : "s"}`,
    reasons.no_supported_signal ? `${reasons.no_supported_signal} no supported signal` : null,
    reasons.rejected_non_household ? `${reasons.rejected_non_household} filtered as non-household` : null,
    reasons.already_processed ? `${reasons.already_processed} already processed` : null,
  ].filter(Boolean);
}

function discoverySourceSummaryChips(summary = {}) {
  return [
    ["Active", summary.active],
    ["Connected", summary.connected],
    ["Setup needed", summary.not_connected],
    ["Pending", summary.pending_setup],
    ["Sync healthy", summary.sync_success],
    ["Sync errors", summary.sync_error],
  ].filter(([, value]) => Number(value || 0) > 0);
}

function ImportCandidateCard({ candidate, busy, onApprove, onApproveAndTrust, onMerge, onMergeAndTrust, onResolveMerge, onDismiss, onCorrect, onOpenRecord, onDelete, focused = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [conflictChoices, setConflictChoices] = useState({});
  const meta = IMPORT_MODULE_META[candidate.proposed_module] || { label: titleCase(candidate.proposed_module || "Import"), icon: "◇" };
  const confidence = confidenceMeta(candidate.confidence);
  const reviewMeta = candidateReviewMeta(candidate);
  const possibleDuplicate = candidateIsPossibleDuplicate(candidate);
  const fields = candidateFields(candidate);
  const supported = SUPPORTED_IMPORT_MODULES.has(candidate.proposed_module);
  const mergeSupported = possibleDuplicate && MERGE_SUPPORTED_MODULES.has(candidate.proposed_module) && reviewMeta.matches?.length > 0;
  const financialMergeTrustEligible = AUTO_UPDATE_TRUST_MODULES.has(candidate.proposed_module) && Boolean(candidate.source_account && !["manual", "file_upload", "manual_calendar"].includes(candidate.source_account));
  const bestMatch = reviewMeta.best_match || reviewMeta.matches?.[0];
  const conflicts = bestMatch?.conflicts || [];
  const fieldChanges = bestMatch?.field_changes || [];
  const conflictsResolved = conflicts.length > 0 && conflicts.every(change => ["existing", "discovered"].includes(conflictChoices[change.field]));
  const whyLines = candidateWhyLines(candidate);
  const isPending = (candidate.status || "pending") === "pending";
  const trustEligible = Boolean(candidate.source_account && !["manual", "file_upload", "manual_calendar"].includes(candidate.source_account));
  const auditMeta = candidate.audit_meta || {};
  const amountWarning = candidate.proposed_payload?._amount_warning;
  const decisionLabel = candidate.status === "imported"
    ? `Imported${candidate.imported_record_type ? ` to ${candidate.imported_record_type}` : ""}`
    : (dismissalReasonLabel(candidate.dismissal_reason) || titleCase(candidate.status || "Pending"));
  const recordRoute = routeForImportedCandidate(candidate);
  const editableFields = candidate.proposed_module === "subscriptions"
    ? [
        ["provider", "Provider", "text"], ["amount", "New price", "number"],
        ["previous_amount", "Previous price", "number"], ["billing_cycle", "Billing cycle", "text"],
        ["effective_date", "Effective date", "date"], ["next_renewal_date", "Renewal date", "date"],
        ["trial_end_date", "Trial end", "date"], ["notes", "Notes", "text"],
      ]
    : candidate.proposed_module === "utilities"
      ? [
          ["provider", "Provider", "text"], ["service_type", "Service type", "text"],
          ["amount", "Bill amount", "number"], ["previous_amount", "Previous bill", "number"],
          ["due_date", "Due date", "date"], ["account_number", "Account number", "text"],
          ["usage_amount", "Usage", "number"], ["usage_unit", "Usage unit", "text"],
          ["notes", "Notes", "text"],
        ]
      : [];
  const beginEdit = () => {
    const payload = candidate.proposed_payload || {};
    setDraft(Object.fromEntries(editableFields.map(([key]) => [key, payload[key] ?? ""])));
    setEditing(true);
  };
  return (
    <article id={`discovery-${candidate.id}`} className={`hm-import-card${focused ? " is-focused" : ""}`}>
      <div className="hm-import-card__head">
        <span className="hm-import-card__icon" aria-hidden="true">{meta.icon}</span>
        <div>
          <div className="hm-import-card__module">{meta.label}</div>
          <h3>{candidateTitle(candidate)}</h3>
        </div>
        <span className={`hm-import-confidence is-${confidence.tone}`}>
          {reviewMeta.match_status === "conflict" ? "Decision required" : reviewMeta.match_status === "suggested_update" ? "Matched update" : (possibleDuplicate ? "Possible duplicate" : confidence.label)}
        </span>
      </div>
      <div className="hm-import-card__badges">
        <span className={`hm-import-source-badge${reviewMeta.is_demo ? " is-demo" : ""}`}>
          {reviewMeta.source_label || "Unknown source"}
        </span>
        {possibleDuplicate && <span className="hm-import-source-badge is-duplicate">Already tracked?</span>}
        {reviewMeta.learned_confirmation && <span className="hm-import-source-badge is-learned">Previously confirmed match</span>}
      </div>
      {candidate.raw_subject && (
        <p className="hm-import-card__source">Subject: {candidate.raw_subject}</p>
      )}
      {reviewMeta.source_detail && (
        <p className="hm-import-card__source">{reviewMeta.source_detail}</p>
      )}
      {reviewMeta.detection_reason && (
        <p className="hm-import-card__source">Why found: {reviewMeta.detection_reason}</p>
      )}
      {reviewMeta.ai_gate_reason && (
        <p className="hm-import-card__source">
          {reviewMeta.automation_path === "rules_only" ? "Matched by rule" : "Needs review"}: {reviewMeta.ai_gate_reason}
        </p>
      )}
      {amountWarning && (
        <div className="hm-import-amount-warning" role="alert">
          <strong>Verify this bill amount</strong>
          <p>{amountWarning.message}</p>
          <span>
            Current {moneyValue(amountWarning.current_amount)} · Detected {moneyValue(amountWarning.detected_amount)}
          </span>
        </div>
      )}
      {whyLines.length > 0 && (
        <div className="hm-import-why">
          <strong>Why HouseMind found this</strong>
          <ul>
            {whyLines.map((line, index) => (
              <li key={`${candidate.id}-why-${index}`}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {!isPending && (
        <div className="hm-import-audit">
          {auditMeta.created_at && <span>Found {formatShortDate(auditMeta.created_at)}</span>}
          {auditMeta.decided_at && <span>Decided {formatShortDate(auditMeta.decided_at)}</span>}
          {auditMeta.source_account && <span>Source account: {auditMeta.source_account}</span>}
          {auditMeta.email_id && <span>Source ID: {String(auditMeta.email_id).slice(0, 18)}</span>}
          {auditMeta.raw_snippet && <p>{auditMeta.raw_snippet}</p>}
        </div>
      )}
      {possibleDuplicate && reviewMeta.matches?.length > 0 && (
        <div className={`hm-import-match${conflicts.length ? " has-conflicts" : " is-update"}`}>
          <strong>{conflicts.length ? "Identity details need your decision" : "Suggested existing record"}</strong>
          {reviewMeta.matches.map(match => (
            <span key={`${candidate.id}-${match.record_type}-${match.record_id}`}>
              {match.label} · {Math.round(Number(match.confidence || 0) * 100)}% match · {match.reason}
            </span>
          ))}
          {fieldChanges.length > 0 && (
            <div className="hm-import-change-list">
              {fieldChanges.map(change => (
                <div className={change.kind === "fill_missing" ? "is-fill" : "is-change"} key={`${candidate.id}-change-${change.field}`}>
                  <b>{change.label}</b>
                  <span>{change.existing_value === null || change.existing_value === "" ? "Not saved" : String(change.existing_value)}</span>
                  <i>→</i>
                  <strong>{String(change.discovered_value)}</strong>
                </div>
              ))}
            </div>
          )}
          {conflicts.length > 0 ? (
            <div className="hm-import-conflicts">
              <p>Choose the correct identity value. HouseMind will retain this decision as private household matching evidence.</p>
              {conflicts.map(change => (
                <fieldset key={`${candidate.id}-conflict-${change.field}`}>
                  <legend>{change.label}</legend>
                  <label><input type="radio" name={`${candidate.id}-${change.field}`} checked={conflictChoices[change.field] === "existing"} onChange={() => setConflictChoices(current => ({ ...current, [change.field]: "existing" }))} /> Keep existing: {String(change.existing_value)}</label>
                  <label><input type="radio" name={`${candidate.id}-${change.field}`} checked={conflictChoices[change.field] === "discovered"} onChange={() => setConflictChoices(current => ({ ...current, [change.field]: "discovered" }))} /> Use discovered: {String(change.discovered_value)}</label>
                </fieldset>
              ))}
            </div>
          ) : mergeSupported ? (
            <p>Review the exact changes above, then update the matched record. Nothing changes until you approve.</p>
          ) : (
            <p>This module is duplicate-aware, but safe update rules are not enabled yet. Dismiss it or import only if this is truly separate.</p>
          )}
        </div>
      )}
      {isPending && reviewMeta.learned_confirmation && (
        <p className="hm-import-learned">{reviewMeta.learned_confirmation.message} Nothing will be imported automatically.</p>
      )}
      {reviewMeta.trusted_rule && (
        <p className="hm-import-learned">
          {reviewMeta.trusted_rule.auto_imported
            ? "Handled by an active trusted rule. The source and result remain in Discovery activity."
            : "Matched an active trusted rule, but this item still requires review."}
        </p>
      )}
      {fields.length > 0 && (
        <dl className="hm-import-fields">
          {fields.map(([label, value]) => (
            <div key={`${candidate.id}-${label}`}>
              <dt>{label}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {isPending && editing && (
        <div className="hm-import-edit" aria-label="Correct discovery details">
          <strong>Correct before importing</strong>
          <div className="hm-import-edit__grid">
            {editableFields.map(([key, label, type]) => (
              <label key={`${candidate.id}-edit-${key}`}>
                <span>{label}</span>
                <input
                  type={type}
                  step={type === "number" ? "0.01" : undefined}
                  value={draft[key] ?? ""}
                  onChange={event => setDraft(current => ({ ...current, [key]: event.target.value }))}
                />
              </label>
            ))}
          </div>
          <div className="hm-import-edit__actions">
            <button type="button" className="hm-import-btn hm-import-btn--ghost" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="hm-import-btn hm-import-btn--primary" disabled={busy} onClick={async () => {
              const saved = await onCorrect(candidate, draft);
              if (saved) setEditing(false);
            }}>{busy ? "Saving..." : "Save corrections"}</button>
          </div>
        </div>
      )}
      {!isPending && (
        <div className="hm-import-card__decision">
          <span className={`hm-import-card__status is-${candidate.status || "pending"}`}>
            {decisionLabel}
          </span>
          {candidate.status === "imported" && recordRoute && (
            <button type="button" className="hm-import-btn hm-import-btn--ghost" onClick={() => onOpenRecord?.(recordRoute)}>
              Open record
            </button>
          )}
          {["dismissed", "expired"].includes(candidate.status) && (
            <button type="button" className="hm-import-btn hm-import-btn--danger" onClick={() => onDelete?.(candidate)}>
              Delete permanently
            </button>
          )}
        </div>
      )}
      {isPending && (
        <div className="hm-import-card__actions">
          <div className="hm-import-card__action-heading">
            <span>Choose what happens</span>
            <small>HouseMind waits for your approval.</small>
          </div>
          {editableFields.length > 0 && !editing && (
            <button type="button" className="hm-import-btn hm-import-btn--ghost hm-import-btn--edit" disabled={busy} onClick={beginEdit}>Edit details</button>
          )}
          <button type="button" className="hm-import-btn hm-import-btn--ghost hm-import-btn--dismiss" disabled={busy} onClick={() => onDismiss(candidate, possibleDuplicate ? "duplicate" : "user_dismissed")}>
            {possibleDuplicate ? "Dismiss duplicate" : "Dismiss"}
          </button>
          {mergeSupported && conflicts.length === 0 && (
            <button type="button" className="hm-import-btn hm-import-btn--primary" disabled={busy} onClick={() => onMerge(candidate, reviewMeta.matches[0])}>
              {busy ? "Updating..." : "Update existing"}
            </button>
          )}
          {mergeSupported && conflicts.length === 0 && financialMergeTrustEligible && onMergeAndTrust && (
            <button type="button" className="hm-import-btn hm-import-btn--trust" aria-label="Update and trust future statements" disabled={busy} onClick={() => onMergeAndTrust(candidate, reviewMeta.matches[0])}>
              <span aria-hidden="true">✦</span>{busy ? "Updating..." : "Update + trust future statements"}
            </button>
          )}
          {mergeSupported && conflicts.length > 0 && (
            <button type="button" className="hm-import-btn hm-import-btn--primary" disabled={busy || !conflictsResolved} onClick={() => onResolveMerge(candidate, bestMatch, conflictChoices)}>
              {busy ? "Updating..." : "Apply decisions & update"}
            </button>
          )}
          <button
            type="button"
            className={`hm-import-btn hm-import-btn--accept ${possibleDuplicate && mergeSupported ? "hm-import-btn--ghost" : "hm-import-btn--primary"}`}
            disabled={busy || !supported}
            title={supported ? "Accept import" : "Approval for this module comes in the next backend slice"}
            onClick={() => supported && onApprove(candidate)}
          >
            {supported ? (busy ? "Saving..." : (possibleDuplicate ? "Create separate" : "Accept once")) : "Coming next"}
          </button>
          {supported && trustEligible && !possibleDuplicate && onApproveAndTrust && (
            <button
              type="button"
              className="hm-import-btn hm-import-btn--trust"
              aria-label={["events", "pantry", "vault", "vehicles"].includes(candidate.proposed_module) ? "Accept + automate future" : "Accept + remember source"}
              disabled={busy}
              onClick={() => onApproveAndTrust(candidate)}
            >
              <span aria-hidden="true">✦</span>{busy ? "Saving..." : (["events", "pantry", "vault", "vehicles"].includes(candidate.proposed_module) ? "Accept + automate future" : "Accept + remember source")}
            </button>
          )}
          {supported && trustEligible && !possibleDuplicate && onApproveAndTrust && (
            <small className="hm-import-card__trust-note">
              {["events", "pantry", "vault", "vehicles"].includes(candidate.proposed_module)
                ? "Only future high-confidence matches from this account will be automatic."
                : "Future financial changes still come back to you for review."}
            </small>
          )}
          {mergeSupported && conflicts.length === 0 && financialMergeTrustEligible && onMergeAndTrust && (
            <small className="hm-import-card__trust-note">
              {mergeTrustNote(candidate.proposed_module)}
            </small>
          )}
        </div>
      )}
    </article>
  );
}

function DiscoverySourceCard({ source, busy, onAction }) {
  const isForwarding = source.provider === "forwarding";
  const isOtherEmail = source.provider === "other_email";
  const imapPresets = {
    yahoo: {
      label: "Yahoo",
      host: "imap.mail.yahoo.com",
      port: "993",
      help: "Use a Yahoo app password from Account Security.",
    },
    icloud: {
      label: "iCloud",
      host: "imap.mail.me.com",
      port: "993",
      help: "Use an Apple app-specific password from Apple ID settings.",
    },
    fastmail: {
      label: "Fastmail",
      host: "imap.fastmail.com",
      port: "993",
      help: "Use a Fastmail app password with IMAP access enabled.",
    },
    custom: {
      label: "Custom IMAP",
      host: "",
      port: "993",
      help: "Use your provider's IMAP host, port, and app password.",
    },
  };
  const [imapOpen, setImapOpen] = useState(false);
  const [imapDraft, setImapDraft] = useState({
    preset: "custom",
    source_account: source.source_account || "",
    imap_host: "",
    imap_port: "993",
    imap_username: source.source_account || "",
    imap_password: "",
    imap_mailbox: "INBOX",
    imap_use_ssl: true,
  });
  const isGoogle = source.provider === "gmail" || source.provider === "google_calendar";
  const isMicrosoft = source.provider === "outlook" || source.provider === "outlook_calendar";
  const isOauthProvider = isGoogle || isMicrosoft;
  const isConnected = source.permission_status === "connected";
  const canRequest = source.permission_status !== "not_required" && !isConnected && source.permission_status !== "pending_user_setup";
  const canDisable = source.permission_status !== "not_required" && source.status !== "disabled";
  const canDisconnect = source.permission_status !== "not_required" && (source.source_account || source.permission_status !== "not_connected");
  const scopes = Array.isArray(source.scopes) ? source.scopes : [];
  const syncMeta = syncStatusMeta(source);
  const sourceStateLabel = isConnected
    ? "Connected"
    : source.permission_status === "not_required"
      ? "Ready"
      : source.status === "planned"
        ? "Available"
        : source.status === "disabled"
          ? "Off"
          : titleCase(source.status);
  const cursorLabel = syncCursorLabel(source.sync_cursor || {});
  const runSummary = syncRunSummary(source.sync_cursor || {});
  const updateImapDraft = (patch) => setImapDraft(draft => ({ ...draft, ...patch }));
  const applyImapPreset = (presetKey) => {
    const preset = imapPresets[presetKey] || imapPresets.custom;
    setImapDraft(draft => ({
      ...draft,
      preset: presetKey,
      imap_host: preset.host,
      imap_port: preset.port,
      imap_use_ssl: true,
    }));
  };
  const submitImap = (event) => {
    event.preventDefault();
    onAction(source, "configure_imap", imapDraft);
  };
  const selectedPreset = imapPresets[imapDraft.preset] || imapPresets.custom;
  return (
    <article id={`discovery-source-${source.provider}`} className={`hm-import-source is-${source.status}`} key={source.id || `${source.provider}-${source.source_type}`}>
      <div className="hm-import-source__identity">
        <strong>{source.label}</strong>
        <span>{titleCase(source.provider)} · {titleCase(source.source_type)}</span>
      </div>
      <div className="hm-import-source__state">
        <em>{sourceStateLabel}</em>
        {syncMeta && <b className={`hm-import-source__sync is-${syncMeta.tone}`}>{syncMeta.label}</b>}
      </div>
      <p>{source.access_level}</p>
      <div className="hm-import-source__meta">
        {source.permission_status && <span>Permission: {titleCase(source.permission_status)}</span>}
        {source.source_account && <span>Account: {source.source_account}</span>}
        {source.last_sync_attempt_at && <span>Last attempt: {formatShortDate(source.last_sync_attempt_at)}</span>}
        {source.last_synced_at && <span>Last sync: {formatShortDate(source.last_synced_at)}</span>}
        {cursorLabel && <span>{cursorLabel}</span>}
        {scopes.length > 0 && <span>Scopes: {scopes.join(", ")}</span>}
      </div>
      {runSummary.length > 0 && <div className="hm-import-source__run"><strong>Latest scan</strong><div>{runSummary.map(item => <span key={item}>{item}</span>)}</div><small>Only counts and reason codes are retained here—not email contents.</small></div>}
      {isForwarding && source.source_account && (
        <div className="hm-import-source__forwarding">
          <strong>{source.source_account}</strong>
          <span>Forward household receipts, bills, warranties, and confirmations here. HouseMind will place findings in review before anything is saved.</span>
          <button type="button" onClick={() => navigator.clipboard?.writeText(source.source_account)}>
            Copy address
          </button>
        </div>
      )}
      {source.last_sync_error && <p className="hm-import-source__error">Sync error: {source.last_sync_error}</p>}
      {isOtherEmail && (
        <div className="hm-import-source__imap">
          <button type="button" onClick={() => setImapOpen(open => !open)} disabled={busy}>
            {imapOpen ? "Hide IMAP setup" : (source.permission_status === "connected" ? "Update IMAP setup" : "Set up IMAP")}
          </button>
          {imapOpen && (
            <form onSubmit={submitImap}>
              <label>
                <span>Email provider</span>
                <select
                  value={imapDraft.preset}
                  onChange={(event) => applyImapPreset(event.target.value)}
                >
                  {Object.entries(imapPresets).map(([key, preset]) => (
                    <option key={key} value={key}>{preset.label}</option>
                  ))}
                </select>
              </label>
              <p className="hm-import-source__imap-help">{selectedPreset.help}</p>
              <label>
                <span>Email account</span>
                <input
                  type="email"
                  value={imapDraft.source_account}
                  onChange={(event) => updateImapDraft({ source_account: event.target.value, imap_username: event.target.value })}
                  placeholder="family@example.com"
                  autoComplete="username"
                />
              </label>
              <label>
                <span>IMAP host</span>
                <input
                  type="text"
                  value={imapDraft.imap_host}
                  onChange={(event) => updateImapDraft({ imap_host: event.target.value })}
                  placeholder="imap.mail.yahoo.com"
                  autoComplete="off"
                />
              </label>
              <label>
                <span>App password</span>
                <input
                  type="password"
                  value={imapDraft.imap_password}
                  onChange={(event) => updateImapDraft({ imap_password: event.target.value })}
                  placeholder="Provider app password"
                  autoComplete="current-password"
                />
              </label>
              <div className="hm-import-source__imap-row">
                <label>
                  <span>Port</span>
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    value={imapDraft.imap_port}
                    onChange={(event) => updateImapDraft({ imap_port: event.target.value })}
                  />
                </label>
                <label>
                  <span>Mailbox</span>
                  <input
                    type="text"
                    value={imapDraft.imap_mailbox}
                    onChange={(event) => updateImapDraft({ imap_mailbox: event.target.value })}
                    placeholder="INBOX"
                  />
                </label>
              </div>
              <label className="hm-import-source__check">
                <input
                  type="checkbox"
                  checked={!!imapDraft.imap_use_ssl}
                  onChange={(event) => updateImapDraft({ imap_use_ssl: event.target.checked })}
                />
                <span>Use SSL</span>
              </label>
              <button type="submit" disabled={busy}>
                {busy ? "Saving..." : "Save IMAP setup"}
              </button>
            </form>
          )}
        </div>
      )}
      {source.permission_status !== "not_required" && (
        <div className="hm-import-source__actions">
          {isConnected && (
            <button type="button" className="is-primary" disabled={busy} onClick={() => onAction(source, "sync")}>{busy ? "Checking..." : "Sync now"}</button>
          )}
          {isConnected && source.provider === "gmail" && (
            <button type="button" className="is-secondary" disabled={busy} onClick={() => onAction(source, "recheck")}>Recheck last 30 days</button>
          )}
          {canRequest && (
            <button type="button" className="is-primary" disabled={busy} onClick={() => onAction(source, "request")}>
              {busy ? "Connecting..." : (isForwarding ? "Generate address" : ["oauth", "calendar_oauth", "document_oauth"].includes(source.source_type) ? "Connect" : "Request setup")}
            </button>
          )}
          {isConnected && isGoogle && (
            <button type="button" className="is-secondary" disabled={busy} onClick={() => onAction(source, "request")}>Change Google account</button>
          )}
          {isConnected && isMicrosoft && (
            <button type="button" className="is-secondary" disabled={busy} onClick={() => onAction(source, "request")}>Change Microsoft account</button>
          )}
          {canDisable && (
            <button type="button" className="is-tertiary" disabled={busy} onClick={() => onAction(source, "disable")}>
              Disable
            </button>
          )}
          {canDisconnect && (
            <button type="button" className="is-danger" disabled={busy} onClick={() => onAction(source, "disconnect")}>
              Disconnect
            </button>
          )}
        </div>
      )}
      {isConnected && isOauthProvider && (
        <small className="hm-import-source__account-note">One {isGoogle ? "Google" : "Microsoft"} account is active for this connector. Use “Change {isGoogle ? "Google" : "Microsoft"} account” to replace it.</small>
      )}
    </article>
  );
}

function FoundByHouseMindDrawer({
  open,
  candidates,
  loading,
  busyId,
  notice,
  error,
  intakeOpen,
  intakeDraft,
  intakeBusy,
  intakeFileBusy,
  historyOpen,
  history,
  historyLoading,
  historyFilters,
  historyMeta,
  sourcesOpen,
  sourcesPayload,
  sourcesLoading,
  sourceBusyId,
  trustedRules,
  trustedRulesLoading,
  onClose,
  onRefresh,
  onApprove,
  onApproveAndTrust,
  onMerge,
  onMergeAndTrust,
  onResolveMerge,
  onCorrect,
  onDismiss,
  onApproveHighConfidence,
  onToggleIntake,
  onToggleHistory,
  onToggleSources,
  onSourceAction,
  onDisableTrustedRule,
  onHistoryFilterChange,
  onDeleteHistory,
  onIntakeDraftChange,
  onSubmitIntake,
  onUploadIntakeFile,
  onOpenRecord,
  focusedCandidateId,
}) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [showAllSources, setShowAllSources] = useState(false);
  if (!open) return null;
  const groups = groupCandidates(candidates);
  const historyGroups = groupCandidates(history);
  const statusOptions = filterOptionsFromCounts(historyMeta?.statuses, ["pending", "imported", "dismissed", "expired"]);
  const sourceOptions = filterOptionsFromCounts(historyMeta?.sources, ["seed", "email_discovery", "image_discovery", "calendar_discovery"]);
  const accountOptions = filterOptionsFromCounts(historyMeta?.source_accounts, []);
  const moduleOptions = filterOptionsFromCounts(historyMeta?.modules, ["subscriptions", "utilities", "vault", "pantry", "vehicles", "events"]);
  const reasonOptions = filterOptionsFromCounts(historyMeta?.dismissal_reasons, []);
  const sourceSummaryChips = discoverySourceSummaryChips(sourcesPayload?.summary || {});
  const allSources = sourcesPayload?.sources || [];
  const visibleSources = showAllSources
    ? allSources
    : allSources.filter(source => source.permission_status === "connected" || source.permission_status === "pending_user_setup" || source.last_sync_status === "error");
  const hiddenSourceCount = Math.max(0, allSources.length - visibleSources.length);
  const revealSource = (provider) => {
    setShowAllSources(true);
    window.setTimeout(() => document.getElementById(`discovery-source-${provider}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };
  const highCount = candidates.filter(candidate =>
    Number(candidate.confidence || 0) >= 0.9 && SUPPORTED_IMPORT_MODULES.has(candidate.proposed_module) && !candidateIsPossibleDuplicate(candidate)
  ).length;
  return (
    <div className="hm-import-drawer-shell" role="dialog" aria-modal="true" aria-label="Discovery inbox">
      <button type="button" className="hm-import-drawer-scrim" aria-label="Close discovery inbox" onClick={onClose} />
      <aside className="hm-import-drawer">
        <div className="hm-import-drawer__head">
          <div>
            <p>Discovery inbox</p>
            <h2>Confirm found information</h2>
          </div>
          <button type="button" className="hm-import-drawer__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="hm-import-drawer__toolbar">
          <div className="hm-import-drawer__toolbar-copy">
            <span>{candidates.length} pending</span>
            <small>Review the source and destination. Nothing changes until you approve.</small>
          </div>
          <div className="hm-import-drawer__toolbar-actions">
            <button type="button" className="is-icon" onClick={onRefresh} disabled={loading} aria-label="Refresh discovery inbox">↻</button>
            <button type="button" className="is-secondary" onClick={() => setToolsOpen(value => !value)}>
              {toolsOpen ? "Close controls" : "Inbox controls"}
            </button>
          </div>
        </div>
        {toolsOpen && (
          <div className="hm-import-drawer__tools">
            <button type="button" className="is-primary" onClick={onToggleIntake} disabled={loading || intakeBusy}>
              {intakeOpen ? "Close add information" : "+ Add information"}
            </button>
            <button type="button" className="is-secondary" onClick={onToggleHistory} disabled={loading || historyLoading}>
              {historyOpen ? "Close history" : "Decision history"}
            </button>
            <button type="button" className="is-secondary" onClick={onToggleSources} disabled={sourcesLoading}>
              {sourcesOpen ? "Close connections" : "Manage connections"}
            </button>
            {highCount > 0 && (
              <button type="button" onClick={onApproveHighConfidence} disabled={loading}>
                Import clear matches ({highCount})
              </button>
            )}
            <div className="hm-trusted-rules">
              <div><strong>Trusted automation</strong><small>{trustedRulesLoading ? "Loading…" : `${trustedRules.length} active household rule${trustedRules.length === 1 ? "" : "s"}`}</small></div>
              {!trustedRulesLoading && trustedRules.length > 0 && (
                <div className="hm-trusted-rules__list">
                  {trustedRules.map(rule => (
                    <div key={rule.id}>
                      <span><b>{titleCase(rule.provider_key)}</b><small>{IMPORT_MODULE_META[rule.proposed_module]?.label || titleCase(rule.proposed_module)} · {rule.action === "auto_import" ? "Auto-import safe matches" : "Review-first"} · {rule.match_count || 0} handled</small></span>
                      <button type="button" onClick={() => onDisableTrustedRule(rule)} disabled={loading}>Turn off</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {intakeOpen && (
          <form className="hm-import-intake" onSubmit={onSubmitIntake}>
            <div className="hm-import-intake__grid">
              <label>
                <span>Details type</span>
                <select
                  value={intakeDraft.intake_type}
                  onChange={event => onIntakeDraftChange({ intake_type: event.target.value })}
                  disabled={intakeBusy}
                >
                  <option value="email">Email</option>
                  <option value="calendar">Calendar</option>
                </select>
              </label>
              <label>
                <span>{intakeDraft.intake_type === "calendar" ? "Calendar source" : "Inbox source"}</span>
                <select
                  value={intakeDraft.provider}
                  onChange={event => onIntakeDraftChange({ provider: event.target.value })}
                  disabled={intakeBusy}
                >
                  <option value="manual">Manual paste</option>
                  <option value="gmail">Gmail</option>
                  <option value="outlook">Outlook</option>
                  <option value="google_calendar">Google Calendar</option>
                  <option value="outlook_calendar">Outlook Calendar</option>
                  <option value="icloud">iCloud Mail</option>
                  <option value="yahoo">Yahoo Mail</option>
                  <option value="forwarded">Forwarded email</option>
                </select>
              </label>
              <label>
                <span>Account</span>
                <input
                  value={intakeDraft.source_account}
                  onChange={event => onIntakeDraftChange({ source_account: event.target.value })}
                  placeholder="Optional"
                  disabled={intakeBusy}
                />
              </label>
            </div>
            <label>
              <span>{intakeDraft.intake_type === "calendar" ? "Title" : "Subject"}</span>
              <input
                value={intakeDraft.subject}
                onChange={event => onIntakeDraftChange({ subject: event.target.value })}
                placeholder={intakeDraft.intake_type === "calendar" ? "Flight, trip, birthday, appointment, or event title" : "Receipt, bill, renewal, trip, or warranty email"}
                disabled={intakeBusy}
              />
            </label>
            <label>
              <span>{intakeDraft.intake_type === "calendar" ? "Calendar details" : "Email text"}</span>
              <textarea
                rows={5}
                value={intakeDraft.body}
                onChange={event => onIntakeDraftChange({ body: event.target.value })}
                placeholder={intakeDraft.intake_type === "calendar" ? "Paste date, location, destination, notes, or provider event details. HouseMind only creates review items." : "Paste the email body here. HouseMind only creates review items; nothing is saved until you approve."}
                disabled={intakeBusy}
              />
            </label>
            <div className="hm-import-intake__actions">
              <span>Detect, review, approve. No automatic writes.</span>
              <button type="submit" className="hm-import-btn hm-import-btn--primary" disabled={intakeBusy || (!intakeDraft.subject.trim() && !intakeDraft.body.trim())}>
                {intakeBusy ? "Analyzing..." : "Find items"}
              </button>
            </div>
            <label className="hm-import-intake__file">
              <span>{intakeFileBusy ? "Reading file..." : "Upload email, PDF, receipt, bill, or screenshot"}</span>
              <input
                type="file"
                accept=".eml,.txt,.text,.md,.csv,.pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,message/rfc822,text/*,application/pdf,image/jpeg,image/png,image/webp,image/gif,image/heic"
                disabled={intakeFileBusy}
                onChange={onUploadIntakeFile}
              />
            </label>
          </form>
        )}
        {(notice || error) && (
          <div className={`hm-import-alert${error ? " is-error" : " is-success"}`} role="status">
            {error || notice}
          </div>
        )}
        <div className="hm-import-drawer__body">
          {sourcesOpen && (
            <section className="hm-import-sources">
              <div className="hm-import-group__title">
                <span>◇</span>
                <strong>Connected accounts &amp; sources</strong>
                <em>{sourcesPayload?.sources?.length || 0}</em>
              </div>
              {sourcesLoading && (
                <div className="hm-import-loading hm-import-loading--compact">
                  <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  Loading sources...
                </div>
              )}
              {!sourcesLoading && sourcesPayload?.permission_model && (
                <p className="hm-import-sources__permission">{sourcesPayload.permission_model}</p>
              )}
              {!sourcesLoading && (
                <nav className="hm-import-sources__shortcuts" aria-label="Connection shortcuts">
                  <button type="button" onClick={() => revealSource("other_email")}><span>＋</span><strong>Add another email inbox</strong><small>Yahoo, iCloud, Fastmail, or IMAP</small></button>
                  <button type="button" onClick={() => revealSource("google_calendar")}><span>◇</span><strong>Manage Google Calendar</strong><small>Connect, sync, or change account</small></button>
                </nav>
              )}
              {!sourcesLoading && sourceSummaryChips.length > 0 && (
                <div className="hm-import-sources__summary">
                  {sourceSummaryChips.map(([label, value]) => (
                    <span key={label}>{label}: {value}</span>
                  ))}
                </div>
              )}
              {!sourcesLoading && (
                <div className="hm-import-sources__grid">
                  {visibleSources.map(source => (
                    <DiscoverySourceCard
                      key={source.id || `${source.provider}-${source.source_type}`}
                      source={source}
                      busy={sourceBusyId === source.id}
                      onAction={onSourceAction}
                    />
                  ))}
                </div>
              )}
              {!sourcesLoading && hiddenSourceCount > 0 && (
                <button type="button" className="hm-import-sources__toggle" onClick={() => setShowAllSources(true)}>
                  Show {hiddenSourceCount} available or built-in source{hiddenSourceCount === 1 ? "" : "s"}
                </button>
              )}
              {!sourcesLoading && showAllSources && allSources.length > 0 && (
                <button type="button" className="hm-import-sources__toggle" onClick={() => setShowAllSources(false)}>
                  Show connected sources only
                </button>
              )}
            </section>
          )}
          {historyOpen && (
            <section className="hm-import-history">
              <div className="hm-import-group__title">
                <span>✓</span>
                <strong>Discovery activity</strong>
                <em>{history.length}</em>
              </div>
              <p className="hm-import-history__note">Showing the latest 25 items. Imported items remain as audit evidence; dismissed or expired items can be deleted.</p>
              <div className="hm-import-history-filters">
                <label>
                  <span>Status</span>
                  <select value={historyFilters.status} onChange={event => onHistoryFilterChange({ status: event.target.value })}>
                    <option value="">All activity</option>
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>{titleCase(option.value)} ({option.count})</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Source</span>
                  <select value={historyFilters.source} onChange={event => onHistoryFilterChange({ source: event.target.value })}>
                    <option value="">All</option>
                    {sourceOptions.map(option => (
                      <option key={option.value} value={option.value}>{titleCase(option.value)} ({option.count})</option>
                    ))}
                  </select>
                </label>
                {accountOptions.length > 0 && (
                  <label>
                    <span>Account</span>
                    <select value={historyFilters.source_account} onChange={event => onHistoryFilterChange({ source_account: event.target.value })}>
                      <option value="">All</option>
                      {accountOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.value} ({option.count})</option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  <span>Module</span>
                  <select value={historyFilters.module} onChange={event => onHistoryFilterChange({ module: event.target.value })}>
                    <option value="">All</option>
                    {moduleOptions.map(option => (
                      <option key={option.value} value={option.value}>{IMPORT_MODULE_META[option.value]?.label || titleCase(option.value)} ({option.count})</option>
                    ))}
                  </select>
                </label>
                {reasonOptions.length > 0 && (
                  <label>
                    <span>Reason</span>
                    <select value={historyFilters.dismissal_reason} onChange={event => onHistoryFilterChange({ dismissal_reason: event.target.value })}>
                      <option value="">All</option>
                      {reasonOptions.map(option => (
                        <option key={option.value} value={option.value}>{dismissalReasonLabel(option.value) || titleCase(option.value)} ({option.count})</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {historyLoading && (
                <div className="hm-import-loading hm-import-loading--compact">
                  <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  Loading history...
                </div>
              )}
              {!historyLoading && history.length === 0 && (
                <div className="hm-import-empty hm-import-empty--compact">
                  <strong>No decisions yet.</strong>
                  <span>Accepted and dismissed discovery items will appear here.</span>
                </div>
              )}
              {!historyLoading && Object.entries(historyGroups).map(([module, rows]) => {
                const meta = IMPORT_MODULE_META[module] || { label: titleCase(module), icon: "◇" };
                return (
                  <section className="hm-import-group" key={`history-${module}`}>
                    <div className="hm-import-group__title">
                      <span>{meta.icon}</span>
                      <strong>{meta.label}</strong>
                      <em>{rows.length}</em>
                    </div>
                    <div className="hm-import-group__list">
                      {rows.map(candidate => (
                        <ImportCandidateCard
                          key={candidate.id}
                          candidate={candidate}
                          busy={false}
                          onApprove={onApprove}
                          onApproveAndTrust={onApproveAndTrust}
                          onMerge={onMerge}
                          onMergeAndTrust={onMergeAndTrust}
                          onResolveMerge={onResolveMerge}
                          onCorrect={onCorrect}
                          onDismiss={onDismiss}
                          onOpenRecord={onOpenRecord}
                          onDelete={onDeleteHistory}
                          focused={Number(focusedCandidateId) === Number(candidate.id)}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </section>
          )}
          {loading && (
            <div className="hm-import-loading">
              <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
              Loading import candidates...
            </div>
          )}
          {!loading && candidates.length === 0 && (
            <div className="hm-import-empty">
              <strong>Nothing waiting right now.</strong>
              <span>Paste or upload email text when you want HouseMind to look for possible household items.</span>
            </div>
          )}
          {!loading && Object.entries(groups).map(([module, rows]) => {
            const meta = IMPORT_MODULE_META[module] || { label: titleCase(module), icon: "◇" };
            return (
              <section className="hm-import-group" key={module}>
                <div className="hm-import-group__title">
                  <span>{meta.icon}</span>
                  <strong>{meta.label}</strong>
                  <em>{rows.length}</em>
                </div>
                <div className="hm-import-group__list">
                  {rows.map(candidate => (
                    <ImportCandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      busy={busyId === candidate.id || busyId === "batch"}
                      onApprove={onApprove}
                      onApproveAndTrust={onApproveAndTrust}
                      onMerge={onMerge}
                      onMergeAndTrust={onMergeAndTrust}
                      onResolveMerge={onResolveMerge}
                      onCorrect={onCorrect}
                      onDismiss={onDismiss}
                      onOpenRecord={onOpenRecord}
                      focused={Number(focusedCandidateId) === Number(candidate.id)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </aside>
    </div>
  );
}

// ─── Widget: Food Snapshot ────────────────────────────────────────────────────

function FoodWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.widgetFood().then(setData).catch(() => {}); }, []);
  const todayMeals = Array.isArray(data?.today_meals) && data.today_meals.length
    ? data.today_meals
    : (data?.today_meal ? [data.today_meal] : []);
  const expiringSoon = data?.expiring_soon ?? data?.expiring_7d ?? data?.expiring_3d ?? 0;

  return (
    <WidgetCard title="🍽 Food snapshot" action="Pantry" onAction={() => navigateToApp(navigate, "/food")} loading={!data}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 12, justifyContent: "space-around" }}>
            <WidgetStat value={data.total_items} label="items tracked" color="var(--hm-mint)" />
            <WidgetStat value={expiringSoon} label="expiring soon" color={expiringSoon > 0 ? "#ff4d4d" : "var(--hm-muted)"} />
          </div>
          <TodayMealsGrid meals={todayMeals} />
        </div>
      )}
    </WidgetCard>
  );
}

// ─── Widget: Occasions ────────────────────────────────────────────────────────

function OccasionsWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.widgetOccasions().then(setData).catch(() => {}); }, []);

  return (
    <WidgetCard title="🎁 People & Gifts" action="Open" onAction={() => navigateToApp(navigate, "/people")} loading={!data}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 12, justifyContent: "space-around" }}>
            <WidgetStat value={data.people_count || 0} label="people" color="var(--hm-mint)" />
            <WidgetStat value={`$${(data.annual_spend || 0).toFixed(0)}`} label="this year" color="var(--hm-cyan)" />
          </div>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--hm-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginTop: 2,
          }}>
            Upcoming gift dates
          </div>
          {data.occasions?.length === 0 && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--hm-muted)", textAlign: "center" }}>
              No gift dates in next 90 days
            </p>
          )}
          {data.occasions?.slice(0, 4).map((occ, i) => (
            <div key={i} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "7px 10px",
              borderRadius: 9,
              background: "var(--hm-surface)",
              fontSize: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: "var(--hm-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {occ.person_name}
                </div>
                <div style={{ color: "var(--hm-muted)", textTransform: "capitalize" }}>
                  {(occ.occasion_type || "occasion").replace(/_/g, " ")}
                </div>
              </div>
              <span style={{
                fontWeight: 700,
                color: occ.days_until <= 7 ? "#ff4d4d" : occ.days_until <= 14 ? "#f5a623" : "var(--hm-muted)",
                flexShrink: 0,
                marginLeft: 8,
              }}>
                {occ.days_until === 0 ? "Today" : occ.days_until === 1 ? "Tmrw" : `${occ.days_until}d`}
              </span>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

// ─── Widget: Vault Health ─────────────────────────────────────────────────────

function VaultWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.widgetVault().then(setData).catch(() => {}); }, []);

  return (
    <WidgetCard title="🏠 Vault health" action="Vault" onAction={() => navigateToApp(navigate, "/home-vault")} loading={!data}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 12, justifyContent: "space-around" }}>
            <WidgetStat value={data.total_items} label="items tracked" color="var(--hm-cyan)" />
            <WidgetStat value={data.action_needed || 0} label="need review" color={data.action_needed > 0 ? "#f5a623" : "var(--hm-muted)"} />
            <WidgetStat value={data.appliance_items || 0} label="appliances" color="var(--hm-mint)" />
          </div>
          {(data.action_items?.length > 0 || data.expiring_items?.length > 0) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
              {(data.action_items?.length ? data.action_items : data.expiring_items).slice(0, 3).map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--hm-muted)" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{item.name}</span>
                  <span style={{ color: item.days <= 14 ? "#ff4d4d" : "#f5a623" }}>
                    {item.days < 0 ? `${Math.abs(item.days)}d overdue` : `${item.days}d`}
                  </span>
                </div>
              ))}
            </div>
          )}
          {data.action_needed === 0 && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--hm-muted)", textAlign: "center" }}>All warranties current ✓</p>
          )}
        </div>
      )}
    </WidgetCard>
  );
}

// ─── Widget: Vehicle Status ───────────────────────────────────────────────────

function VehiclesWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.widgetVehicles().then(setData).catch(() => {}); }, []);

  return (
    <WidgetCard title="🚗 Vehicle status" action="Vehicles" onAction={() => navigateToApp(navigate, "/vehicles")} loading={!data}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {data.vehicles?.length === 0 && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--hm-muted)", textAlign: "center" }}>No vehicles tracked yet</p>
          )}
          {data.vehicles?.slice(0, 3).map((v, i) => (
            <div key={i} style={{ padding: "8px 10px", borderRadius: 9, background: "var(--hm-surface)", fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: "var(--hm-text)", marginBottom: 2 }}>{v.display_name}</div>
              {v.alerts?.length > 0 ? (
                v.alerts.slice(0, 2).map((a, j) => (
                  <div key={j} style={{ color: "#f5a623", marginTop: 2 }}>⚠ {a}</div>
                ))
              ) : (
                <div style={{ color: "var(--hm-muted)" }}>
                  {v.mileage ? `${v.mileage.toLocaleString()} mi` : "No alerts"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

// ─── Widget: Subscription Spend ───────────────────────────────────────────────

// ─── Widget: Events & Trips ───────────────────────────────────────────────────

function EventsWidget() {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);

  useEffect(() => {
    dashboardApi.scheduleWidget(() => eventsApi.listPersonal())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.events || []);
        const today = new Date(); today.setHours(0,0,0,0);
        const upcoming = list
          .map(ev => {
            const d = new Date(ev.start_date || ev.event_date || ev.date || "");
            return { ...ev, _days: Math.round((d - today) / 86400000) };
          })
          .filter(ev => ev._days >= 0)
          .sort((a, b) => a._days - b._days);
        const nextThirtyDays = upcoming.filter(ev => ev._days <= 30);
        // Keep the dashboard snapshot focused. Only reach beyond 30 days when
        // the near-term window is completely empty.
        setEvents((nextThirtyDays.length ? nextThirtyDays : upcoming).slice(0, 3));
      })
      .catch(() => setEvents([]));
  }, []);

  const TRIP_EMOJI = {
    trip: "✈️", concert: "🎵", wedding: "💍", graduation_party: "🎓",
    holiday_gathering: "🎄", game_night: "🎲", anniversary_dinner: "🕯️",
    baby_shower: "🍼", birthday_party: "🎂", other_event: "📅",
  };

  function dayLabel(days) {
    if (days === 0) return "Today";
    if (days === 1) return "Tmrw";
    return `${days}d`;
  }

  function RsvpSummary({ event }) {
    if (!event.rsvp_count) return null;
    const items = [
      event.rsvp_in ? `${event.guest_count || event.rsvp_in} yes` : null,
      event.rsvp_maybe ? `${event.rsvp_maybe} maybe` : null,
      event.rsvp_out ? `${event.rsvp_out} no` : null,
    ].filter(Boolean);
    if (!items.length) return null;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
        {items.map(item => (
          <span key={item} style={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--hm-muted)",
            background: "rgba(15, 23, 42, 0.05)",
            border: "1px solid var(--hm-line)",
            borderRadius: 999,
            padding: "2px 7px",
            lineHeight: 1.4,
          }}>
            {item}
          </span>
        ))}
      </div>
    );
  }

  return (
    <WidgetCard
      title="✈ Events & Trips"
      action="View all"
      onAction={() => navigateToApp(navigate, "/events")}
      loading={events === null}
    >
      {events && events.length === 0 && (
        <p style={{ margin: 0, fontSize: 13, color: "var(--hm-muted)" }}>No upcoming events</p>
      )}
      {(events || []).map(ev => {
        const days = ev._days;
        const urgColor = days <= 3 ? "#ff4d4d" : days <= 14 ? "#f5a623" : "var(--hm-muted)";
        const emoji = TRIP_EMOJI[ev.event_type] || "📅";
        return (
          <div key={ev.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 0",
            borderBottom: "1px solid var(--hm-line)",
            cursor: "pointer",
          }}
            onClick={() => navigateToApp(
              navigate,
              ev.action_url || (ev.event_type === "trip" ? `/trip/event/${ev.id}` : `/events?event_id=${ev.id}`)
            )}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>{emoji}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: "var(--hm-text)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {eventDisplayTitle(ev)}
              </div>
              <div style={{ fontSize: 11, color: "var(--hm-muted)", marginTop: 1 }}>
                {eventSubtitle(ev)}
              </div>
              <RsvpSummary event={ev} />
            </div>
            <div style={{ color: urgColor, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {dayLabel(days)}
            </div>
          </div>
        );
      })}
    </WidgetCard>
  );
}

function SubSpendWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.widgetSubs().then(setData).catch(() => {}); }, []);

  return (
    <WidgetCard title="🔄 Subscription spend" action="Subs" onAction={() => navigateToApp(navigate, "/subscriptions")} loading={!data}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 12, justifyContent: "space-around" }}>
            <WidgetStat value={`$${(data.monthly_total || 0).toFixed(0)}`} label="per month" color="var(--hm-cyan)" />
            <WidgetStat value={data.active_count || 0} label="active subs" color="var(--hm-muted)" />
          </div>
          {data.renewing_items?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--hm-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                Renewing this week
              </div>
              {data.renewing_items.slice(0, 2).map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--hm-muted)", padding: "3px 0" }}>
                  <span>{s.name}</span>
                  <span style={{ color: "#f5a623" }}>${s.amount.toFixed(2)} · {s.days}d</span>
                </div>
              ))}
            </div>
          )}
          {data.renewing_this_week === 0 && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--hm-muted)", textAlign: "center" }}>No renewals this week</p>
          )}
        </div>
      )}
    </WidgetCard>
  );
}

function UtilitiesWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.widgetUtilities().then(setData).catch(() => {}); }, []);

  return (
    <WidgetCard title="⚡ Utilities" action="Open" onAction={() => navigateToApp(navigate, "/utilities")} loading={!data}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 12, justifyContent: "space-around" }}>
            <WidgetStat value={`$${(data.monthly_total || 0).toFixed(0)}`} label="per month" color="var(--hm-cyan)" />
            <WidgetStat value={data.efficiency_score || 100} label="score" color={(data.efficiency_score || 100) < 80 ? "#f5a623" : "var(--hm-mint)"} />
            <WidgetStat value={data.spike_count || 0} label="spikes" color={data.spike_count > 0 ? "#f5a623" : "var(--hm-muted)"} />
          </div>
          {data.top_spike && (
            <div style={{ marginTop: 4, fontSize: 12, color: "var(--hm-muted)", background: "var(--hm-surface)", borderRadius: 8, padding: "7px 9px" }}>
              {data.top_spike.provider} is up {data.top_spike.spike_pct}%.
            </div>
          )}
          {data.due_items?.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--hm-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                Due this week
              </div>
              {data.due_items.map((b, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--hm-muted)", padding: "3px 0" }}>
                  <span>{b.provider}</span>
                  <span style={{ color: "#f5a623" }}>${b.amount.toFixed(2)} · {b.days === 0 ? "Today" : `${b.days}d`}</span>
                </div>
              ))}
            </div>
          )}
          {(data.tracked_count || data.active_count || 0) === 0 && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--hm-muted)", textAlign: "center" }}>No utilities tracked yet</p>
          )}
          {data.tracked_count > 0 && (
            <p style={{ margin: 0, fontSize: 11, color: "var(--hm-muted)", textAlign: "center" }}>
              {data.tracked_count} tracked service{data.tracked_count === 1 ? "" : "s"} included
            </p>
          )}
        </div>
      )}
    </WidgetCard>
  );
}

// ─── Widget: 7-Day Timeline ───────────────────────────────────────────────────

function TimelineWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.widgetTimeline().then(setData).catch(() => {}); }, []);

  function dayLabel(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Tmrw";
    return d.toLocaleDateString("en-US", { weekday: "short" });
  }

  function dayOffset(dateStr) {
    const value = new Date(`${dateStr}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((value - today) / 86400000);
  }

  const moduleRank = {
    cases: 0,
    household: 1,
    property: 2,
    vehicles: 3,
    utilities: 4,
    subs: 5,
    trips: 6,
    events: 7,
    pets: 8,
    vault: 9,
    gifts: 10,
    discoveries: 11,
    predictions: 12,
    food: 13,
  };
  const compareImportance = (a, b) => {
    const priority = (PRIORITY_RANK[a.priority] || 9) - (PRIORITY_RANK[b.priority] || 9);
    if (priority) return priority;
    const module = (moduleRank[a.module] ?? 20) - (moduleRank[b.module] ?? 20);
    if (module) return module;
    return String(a.date).localeCompare(String(b.date));
  };

  const sourceEvents = data?.events || [];
  const pantryByDate = new Map();
  const allEvents = [];
  sourceEvents.forEach(event => {
    const isPantryExpiry = event.module === "food" && event.type === "pantry_expiry";
    if (!isPantryExpiry) {
      allEvents.push(event);
      return;
    }
    // Pantry is a near-term food signal on the dashboard, not a reason to hide
    // harder household deadlines later in the seven-day window.
    if (dayOffset(event.date) > 1) return;
    const pantry = pantryByDate.get(event.date) || [];
    pantry.push(event);
    pantryByDate.set(event.date, pantry);
  });
  pantryByDate.forEach(items => {
    const first = items[0];
    allEvents.push({
      ...first,
      title: `${first.title}${items.length > 1 ? `  +${items.length - 1} more` : ""}`,
      grouped_count: items.length,
    });
  });
  allEvents.sort((a, b) => String(a.date).localeCompare(String(b.date)) || compareImportance(a, b));

  const groupedEvents = new Map();
  allEvents.forEach(event => {
    const group = groupedEvents.get(event.date) || [];
    group.push(event);
    groupedEvents.set(event.date, group);
  });
  groupedEvents.forEach(group => group.sort(compareImportance));

  // First choose the strongest item from as many dates as the compact card can
  // hold. If fewer than five dates are represented, add one more item per date,
  // preferring a different non-food module when one exists.
  const representatives = [...groupedEvents.values()]
    .map(group => group[0])
    .sort(compareImportance)
    .slice(0, 5);
  const previewEvents = [...representatives];
  if (previewEvents.length < 5) {
    const secondRows = [...groupedEvents.values()]
      .map(group => {
        const first = group[0];
        const nonPantry = group.filter(event => event.module !== "food");
        const pool = nonPantry.length > 1 ? nonPantry : group;
        return pool.find(event => event !== first && event.module !== first.module)
          || pool.find(event => event !== first);
      })
      .filter(Boolean)
      .sort(compareImportance);
    previewEvents.push(...secondRows.slice(0, 5 - previewEvents.length));
  }
  previewEvents.sort((a, b) => String(a.date).localeCompare(String(b.date)) || compareImportance(a, b));
  const hiddenEvents = allEvents.filter(event => !previewEvents.includes(event));
  const hiddenDates = new Set(hiddenEvents.map(event => event.date));
  const hiddenLabel = hiddenDates.size === 1
    ? `+${hiddenEvents.length} more ${dayLabel(hiddenEvents[0]?.date).toLowerCase()}`
    : `+${hiddenEvents.length} more in Timeline`;

  return (
    <WidgetCard title="📅 Next 7 days" action="Open" onAction={() => navigateToApp(navigate, "/timeline")} loading={!data}>
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {data.events?.length === 0 && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--hm-muted)", textAlign: "center" }}>Nothing on the radar this week ✓</p>
          )}
          {previewEvents.map((ev, i) => {
            const mod = MODULE_BADGE[ev.module] || MODULE_BADGE.household;
            return (
              <div key={i} style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 8,
                background: "var(--hm-surface)",
                fontSize: 12,
              }}>
                <span style={{ minWidth: 38, fontWeight: 700, color: "var(--hm-muted)", fontSize: 11 }}>
                  {dayLabel(ev.date)}
                </span>
                <span style={{ fontSize: 15, lineHeight: 1 }}>{ev.emoji}</span>
                <span style={{ flex: 1, color: "var(--hm-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {ev.title}
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 6,
                  background: `${mod.color}22`,
                  color: mod.color,
                  letterSpacing: "0.04em",
                  flexShrink: 0,
                }}>
                  {mod.label}
                </span>
              </div>
            );
          })}
          {hiddenEvents.length > 0 && (
            <button
              type="button"
              onClick={() => navigateToApp(navigate, "/timeline")}
              style={{
                alignSelf: "flex-start",
                padding: "3px 0",
                border: 0,
                background: "transparent",
                color: "var(--hm-cyan)",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {hiddenLabel} →
            </button>
          )}
        </div>
      )}
    </WidgetCard>
  );
}

// ─── Extension snapshots: same dashboard contract as the original widgets ───

function HomeCareWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.scheduleWidget(() => homeCareApi.snapshot()).then(setData).catch(() => setData({ summary: {} })); }, []);
  const summary = data?.summary || {};
  return <WidgetCard title="🛠️ Home Care" action="Open" onAction={() => navigateToApp(navigate, "/home-care")} loading={!data}>
    <div className="hm-widget-stats"><WidgetStat value={summary.due_now || 0} label="due now" color={summary.due_now ? "#ff4d4d" : "var(--hm-mint)"}/><WidgetStat value={summary.upcoming || 0} label="upcoming"/><WidgetStat value={summary.active_cases || 0} label="active repairs" color={summary.active_cases ? "#f5a623" : "var(--hm-muted)"}/></div>
    <p className="hm-widget-note">{summary.due_now ? `${summary.due_now} home care item${summary.due_now === 1 ? " needs" : "s need"} attention.` : "Nothing scheduled needs immediate attention."}</p>
  </WidgetCard>;
}

function PropertyProfileWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.scheduleWidget(() => propertyProfileApi.snapshot()).then(setData).catch(() => setData({ summary: {} })); }, []);
  const summary = data?.summary || {};
  return <WidgetCard title="🏠 Property Profile" action="Open" onAction={() => navigateToApp(navigate, "/property-profile")} loading={!data}>
    <div className="hm-widget-stats"><WidgetStat value={`${data?.completeness || 0}%`} label="complete" color={(data?.completeness || 0) < 70 ? "#f5a623" : "var(--hm-mint)"}/><WidgetStat value={summary.spaces || 0} label="spaces"/><WidgetStat value={summary.open_claims || 0} label="open claims" color={summary.open_claims ? "#ff4d4d" : "var(--hm-muted)"}/></div>
    <p className="hm-widget-note">{summary.policies ? `${summary.policies} active property polic${summary.policies === 1 ? "y" : "ies"} connected.` : "No property coverage is connected yet."}</p>
  </WidgetCard>;
}

function SafetyWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.scheduleWidget(() => emergencySafetyApi.snapshot()).then(setData).catch(() => setData({ summary: {} })); }, []);
  const summary = data?.summary || {};
  return <WidgetCard title="🛡️ Emergency & Safety" action="Open" onAction={() => navigateToApp(navigate, "/emergency-safety")} loading={!data}>
    <div className="hm-widget-stats"><WidgetStat value={`${data?.readiness || 0}%`} label="ready" color={(data?.readiness || 0) < 70 ? "#ff4d4d" : "var(--hm-mint)"}/><WidgetStat value={summary.contacts || 0} label="contacts"/><WidgetStat value={summary.checks_due || 0} label="checks due" color={summary.checks_due ? "#f5a623" : "var(--hm-muted)"}/></div>
    <p className="hm-widget-note">{summary.checks_due ? "A household safety check needs attention." : "Saved safety checks are currently clear."}</p>
  </WidgetCard>;
}

function PetsWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.scheduleWidget(() => petsApi.snapshot()).then(setData).catch(() => setData({ summary: {} })); }, []);
  const summary = data?.summary || {};
  return <WidgetCard title="🐾 Pet Care" action="Open" onAction={() => navigateToApp(navigate, "/pets")} loading={!data}>
    <div className="hm-widget-stats"><WidgetStat value={summary.pets || 0} label="pets" color="var(--hm-mint)"/><WidgetStat value={summary.due || 0} label="due now" color={summary.due ? "#ff4d4d" : "var(--hm-muted)"}/><WidgetStat value={summary.upcoming || 0} label="upcoming"/></div>
    <p className="hm-widget-note">{summary.due ? `${summary.due} pet care item${summary.due === 1 ? " is" : "s are"} due.` : "No pet care is due right now."}</p>
  </WidgetCard>;
}

function BudgetWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dashboardApi.scheduleWidget(() => moneyApi.overview()).then(setData).catch(() => setData({ summary: {} })); }, []);
  const summary = data?.summary || {};
  const dollars = value => `$${Number(value || 0).toFixed(0)}`;
  return <WidgetCard title="📊 Household Budget" action="Open" onAction={() => navigateToApp(navigate, "/money/overview")} loading={!data}>
    <div className="hm-widget-stats"><WidgetStat value={dollars(summary.actual)} label="spent" color="var(--hm-mint)"/><WidgetStat value={dollars(summary.committed)} label="committed"/><WidgetStat value={summary.monthly_target == null ? "—" : dollars(summary.remaining)} label={summary.monthly_target == null ? "budget not set" : "remaining"} color={summary.remaining < 0 ? "#ff4d4d" : "var(--hm-muted)"}/></div>
    <p className="hm-widget-note">{summary.missing_amounts ? `${summary.missing_amounts} record${summary.missing_amounts === 1 ? " needs" : "s need"} an amount.` : "Tracked household costs are reconciled."}</p>
  </WidgetCard>;
}

// ─── Health Score Drawer (full detail, slides up from bottom) ────────────────

function HealthScoreDrawer({ health, trend, open, onClose, refreshing, onRefresh }) {
  const navigate = useNavigate();
  const dragStartYRef = useRef(null);
  const dragOffsetRef = useRef(0);
  const closeTimerRef = useRef(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [entered, setEntered] = useState(false);
  const [sheetMaxHeight, setSheetMaxHeight] = useState("min(760px, calc(100dvh - 16px))");

  // Reset drag state every time the drawer opens so stale offset from a previous
  // close animation never makes the sheet appear off-screen on re-open.
  useEffect(() => {
    if (!open) return;
    setDragOffset(0);
    setDragging(false);
    setEntered(false);
    dragStartYRef.current = null;
    dragOffsetRef.current = 0;
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const updateSheetSize = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 720;
      const maxHeight = Math.max(320, Math.min(760, Math.floor(viewportHeight - 8)));
      setSheetMaxHeight(`${maxHeight}px`);
    };
    updateSheetSize();
    window.visualViewport?.addEventListener("resize", updateSheetSize);
    window.visualViewport?.addEventListener("scroll", updateSheetSize);
    window.addEventListener("resize", updateSheetSize);
    return () => {
      window.visualViewport?.removeEventListener("resize", updateSheetSize);
      window.visualViewport?.removeEventListener("scroll", updateSheetSize);
      window.removeEventListener("resize", updateSheetSize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Always unconditionally clear — never restore from a saved snapshot.
      // Saving and restoring creates a race: if the user re-opens before the
      // 160 ms close animation fires onClose(), the snapshot already has
      // position:"fixed" and restoring it permanently freezes the page.
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.documentElement.style.overscrollBehavior = "";
      return undefined;
    }
    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    // Note: do NOT set touchAction on body — the shell's CSS touch-action:none
    // already blocks page scroll; setting it here would prevent the sheet body
    // from scrolling on Android even with touch-action:pan-y on the child.
    document.documentElement.style.overscrollBehavior = "none";
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.documentElement.style.overscrollBehavior = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const closeWithSlide = useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    setDragging(false);
    setEntered(false);
    setDragOffset(window.visualViewport?.height || window.innerHeight);
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      onClose();
    }, 180);
  }, [onClose]);

  const beginDrag = useCallback((clientY) => {
    dragStartYRef.current = clientY;
    dragOffsetRef.current = 0;
    setDragging(true);
  }, []);

  const moveDrag = useCallback((clientY, event) => {
    if (dragStartYRef.current == null) return;
    const next = Math.max(0, clientY - dragStartYRef.current);
    dragOffsetRef.current = next;
    setDragOffset(next);
    if (next > 4) event?.preventDefault?.();
  }, []);

  const endDrag = useCallback(() => {
    if (dragStartYRef.current == null) return;
    dragStartYRef.current = null;
    if (dragOffsetRef.current > 80) {
      closeWithSlide();
      return;
    }
    dragOffsetRef.current = 0;
    setDragging(false);
    setDragOffset(0);
  }, [closeWithSlide]);

  if (!open || !health) return null;

  const modules = Object.entries(health.modules || {});
  const score = Number(health.overall || 0);
  const deductions = Array.isArray(health.deductions) ? health.deductions : [];
  const trendRows = Array.isArray(trend?.snapshots) ? trend.snapshots.slice(-8) : [];
  const trendDelta = Number(trend?.delta || 0);
  const trendColor = trendDelta > 0 ? "var(--hm-mint)" : trendDelta < 0 ? "#ff4d4d" : "var(--hm-muted)";
  const currentTrendScore = trendRows.length ? Number(trendRows[trendRows.length - 1]?.overall_score || score) : score;
  const previousTrendScore = trendRows.length > 1 ? Number(trendRows[trendRows.length - 2]?.overall_score || 0) : null;
  const ringScore = Math.max(0, Math.min(100, Math.round(currentTrendScore || score)));
  const ringColor = ringScore >= 85 ? "var(--hm-mint)" : ringScore >= 70 ? "#f5a623" : "#ff4d4d";
  const weakModules = modules
    .map(([key, module]) => ({
      key,
      label: key === "people" ? "People & Gifts" : key === "subs" ? "Subscriptions" : key.charAt(0).toUpperCase() + key.slice(1),
      score: Number(module.score || 0),
      status: module.status,
    }))
    .filter(item => item.score < 100)
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);

  return (
    <div className="hm-health-sheet-shell" role="dialog" aria-modal="true" aria-label="Household health details">
      <div className="hm-health-sheet-scrim" aria-hidden="true" />
      <section
        className={`hm-health-sheet${entered ? " is-entered" : ""}${dragging ? " is-dragging" : ""}`}
        style={{
          "--hm-health-drag-y": `${dragOffset}px`,
          maxHeight: sheetMaxHeight,
          height: "auto",
        }}
      >
        <div
          className="hm-health-sheet-handle"
          onTouchStart={event => beginDrag(event.touches?.[0]?.clientY || 0)}
          onTouchMove={event => moveDrag(event.touches?.[0]?.clientY || 0, event)}
          onTouchEnd={endDrag}
          onMouseDown={event => beginDrag(event.clientY)}
          onMouseMove={event => dragging && moveDrag(event.clientY, event)}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
        >
          <span />
          <button
            type="button"
            className="hm-health-sheet-close"
            onClick={event => { event.stopPropagation(); closeWithSlide(); }}
            aria-label="Close household health"
          >
            ×
          </button>
        </div>
        <div className="hm-health-sheet-head">
          <div className="hm-health-sheet-title">
            <span>💚 Household health</span>
            <strong>{health.rating || "Score"} · {score}</strong>
          </div>
          <div className="hm-health-sheet-actions">
            <button
              type="button"
              className="hm-health-sheet-refresh"
              onClick={onRefresh}
              disabled={refreshing}
            >
              {refreshing ? "…" : "Refresh"}
            </button>
          </div>
        </div>
        <div className="hm-health-sheet-body">

        {/* Module tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))", gap: 8, marginBottom: 14 }}>
          {modules.map(([key, module]) => {
            const c = module.status === "attention" ? "#ff4d4d" : module.status === "watch" ? "#f5a623" : "var(--hm-mint)";
            return (
              <div key={key} style={{ border: "1px solid var(--hm-line)", borderRadius: 10, padding: "8px 9px", background: "var(--hm-surface)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11, color: "var(--hm-muted)", textTransform: "capitalize" }}>
                  <span>{key === "people" ? "People & Gifts" : key}</span>
                  <strong style={{ color: c }}>{module.score}</strong>
                </div>
                <div style={{ marginTop: 6, height: 5, borderRadius: 999, background: "rgba(100,116,139,0.14)", overflow: "hidden" }}>
                  <span style={{ display: "block", width: `${Math.max(0, Math.min(100, Number(module.score || 0)))}%`, height: "100%", background: c }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="hm-health-ledger">
          <div className="hm-health-ledger__head">
            <div>
              <span>Score ledger</span>
              <strong>{deductions.length ? `${deductions.length} active deduction${deductions.length === 1 ? "" : "s"}` : "No active deductions"}</strong>
            </div>
            <small>Overall impact</small>
          </div>
          {deductions.length ? (
            <div className="hm-health-ledger__rows">
              {deductions.map((item, index) => (
                <button
                  key={`${item.module}-${item.label}-${index}`}
                  type="button"
                  onClick={() => { item.action_url && navigateToApp(navigate, item.action_url); onClose(); }}
                  className="hm-health-ledger__row"
                >
                  <span className={`hm-health-ledger__mark is-${item.severity || "medium"}`} />
                  <span className="hm-health-ledger__copy">
                    <strong>{item.label}</strong>
                    <small>{item.module === "people" ? "People & Gifts" : item.module}</small>
                  </span>
                  <strong className="hm-health-ledger__impact">−{Number(item.weighted_impact || 0).toFixed(1)}</strong>
                </button>
              ))}
            </div>
          ) : (
            <p className="hm-health-ledger__clear">All tracked deterministic checks are currently clear.</p>
          )}
          <p className="hm-health-ledger__note">The score changes when the source record is corrected, paid, completed, or resolved—not when a notification is merely read.</p>
        </div>

        {/* Health trend */}
        <div style={{ border: "1px solid var(--hm-line)", borderRadius: 12, padding: 12, background: "var(--hm-surface)", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <strong style={{ color: "var(--hm-text)", fontSize: 13 }}>📈 Health trend</strong>
            <span style={{ color: trendColor, fontSize: 12, fontWeight: 900 }}>
              {trendRows.length > 1 ? `${trendDelta > 0 ? "+" : ""}${trendDelta} pts` : "Learning"}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "116px minmax(0, 1fr)", gap: 14, alignItems: "center" }}>
            <div style={{
              width: 106,
              height: 106,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: `conic-gradient(${ringColor} ${ringScore * 3.6}deg, rgba(100,116,139,0.14) 0deg)`,
              boxShadow: "inset 0 0 0 1px rgba(100,116,139,0.12)",
            }}>
              <div style={{
                width: 76,
                height: 76,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "var(--hm-card-solid)",
              }}>
                <strong style={{ color: ringColor, fontSize: 24, lineHeight: 1 }}>{ringScore}</strong>
                <small style={{ color: "var(--hm-muted)", fontSize: 10, marginTop: -18 }}>of 100</small>
              </div>
            </div>
            <div>
              <p style={{ margin: "0 0 8px", color: "var(--hm-muted)", fontSize: 12, lineHeight: 1.4 }}>
                {previousTrendScore == null
                  ? "Refresh or let nightly checks run to build your health trend."
                  : `Latest check is ${ringScore}, ${trendDelta > 0 ? "up" : trendDelta < 0 ? "down" : "unchanged"} from ${previousTrendScore}.`}
              </p>
              <strong style={{ display: "block", color: "var(--hm-text)", fontSize: 12, marginBottom: 6 }}>To reach 100</strong>
              <div style={{ display: "grid", gap: 5 }}>
                {(weakModules.length ? weakModules : [{ label: "All modules", score: 100 }]).map(item => (
                  <div key={item.label} style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--hm-muted)", fontSize: 11 }}>
                    <span>{item.score >= 100 ? "Everything is currently complete." : `Improve ${item.label}`}</span>
                    {item.score < 100 && <strong style={{ color: item.score >= 70 ? "#f5a623" : "#ff4d4d" }}>{100 - item.score} pts gap</strong>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Milestones */}
        {(trend?.milestones || []).length > 0 && (
          <div style={{ border: "1px solid var(--hm-line)", borderRadius: 12, padding: 12, background: "var(--hm-surface)", display: "grid", gap: 8 }}>
            <strong style={{ color: "var(--hm-text)", fontSize: 13 }}>Milestones</strong>
            {(trend.milestones || []).slice(0, 3).map(item => (
              <div key={`${item.type}-${item.label}`} style={{ display: "grid", gap: 2, padding: "8px 9px", borderRadius: 10, background: "var(--hm-card)" }}>
                <span style={{ color: item.type === "dropped" ? "#b91c1c" : "var(--hm-mint)", fontSize: 12, fontWeight: 900 }}>{item.label}</span>
                <small style={{ color: "var(--hm-muted)", fontSize: 11, lineHeight: 1.35 }}>{item.detail}</small>
              </div>
            ))}
          </div>
        )}
        </div>
      </section>
    </div>
  );
}

// ─── Health Score Card (compact) ──────────────────────────────────────────────

function HealthScoreCard() {
  const { addToast, updateToast } = useToastActions();
  const [health, setHealth] = useState(null);
  const [trend, setTrend] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadHealth = useCallback(() => {
    dashboardApi.householdHealth().then(setHealth).catch(() => setHealth(null));
    dashboardApi.householdHealthTrend(30).then(setTrend).catch(() => setTrend(null));
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  // The score can now change with no click at all -- vault warranties,
  // vehicle mileage, and other records auto-update via the trusted-rule
  // pipeline in the background. A tab left open should catch up on refocus
  // instead of showing whatever was true when the page first loaded.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      loadHealth();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadHealth]);

  const refresh = async () => {
    setRefreshing(true);
    const toastId = addToast("Rechecking household health…", "loading", { persist: true });
    try {
      const nextHealth = await dashboardApi.refreshHouseholdHealth();
      setHealth(nextHealth);
      setTrend(await dashboardApi.householdHealthTrend(30));
      updateToast(toastId, `Household health updated to ${nextHealth.overall}`, "success");
    } catch (error) {
      updateToast(toastId, error?.message || "Household health could not be refreshed", "error");
    } finally {
      setRefreshing(false);
    }
  };

  if (!health) {
    return (
      <section className="hm-dashboard-summary-card hm-health-card is-loading">
        <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
        Calculating household health...
      </section>
    );
  }

  const modules = Object.entries(health.modules || {});
  const score = Number(health.overall || 0);
  const scoreColor = score >= 85 ? "var(--hm-mint)" : score >= 70 ? "#f5a623" : "#ff4d4d";
  const attentionCount = modules.filter(([, m]) => m.status === "attention").length;

  return (
    <>
      <HealthScoreDrawer
        health={health}
        trend={trend}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        refreshing={refreshing}
        onRefresh={refresh}
      />
      <section className="hm-dashboard-summary-card hm-health-card">
        {/* Score ring */}
        <div className="hm-health-card__ring" style={{ "--health-score-color": scoreColor }}>
          {score}
        </div>

        {/* Label + module status dots */}
        <div className="hm-health-card__summary">
          <p className="hm-dashboard-card__eyebrow">
            Household health
          </p>
          <p className="hm-health-card__title">
            {health.rating || "Score"}
            {attentionCount > 0 && (
              <span>
                · {attentionCount} area{attentionCount === 1 ? "" : "s"} need attention
              </span>
            )}
          </p>
          <div className="hm-health-card__modules">
            {modules.map(([key, module]) => {
              const dotColor = module.status === "attention" ? "#ff4d4d" : module.status === "watch" ? "#f5a623" : "var(--hm-mint)";
              const label = key === "people" ? "people" : key === "subscriptions" ? "subscriptions" : key.replace("_", " ");
              return <span key={key} className="hm-health-card__module" title={`${label}: ${module.score}`}>
                <span className="hm-health-card__dot" style={{ "--module-status-color": dotColor }} />{label}
              </span>;
            })}
          </div>
        </div>

        {/* View details */}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="hm-dashboard-details"
        >
          Details ›
        </button>
      </section>
    </>
  );
}

// ─── Main Dashboard Page ──────────────────────────────────────────────────────

// ─── Cross-module Intelligence Panel ────────────────────────────────────────

const INSIGHT_PRIORITY_META = {
  urgent:  { priority: "urgent" },
  high:    { priority: "soon" },
  savings: { priority: "savings" },
  medium:  { priority: "fyi" },
  low:     { priority: "fyi" },
};

function normalizeInsight(insight) {
  const meta = INSIGHT_PRIORITY_META[insight.priority] || INSIGHT_PRIORITY_META.low;
  return {
    isInsight: true,
    priority: meta.priority,
    title: insight.title,
    description: insight.savings_amount
      ? `${insight.message} Potential saving: $${Number(insight.savings_amount).toFixed(2)}/mo`
      : insight.message,
    action_url: insight.action_url,
    action_label: insight.action_label || "View insight",
    source_module: "insights",
    item_id: insight.id,
    item_type: "housemind_noticed",
    modules: insight.modules || [],
    created_at: insight.created_at,
    original: insight,
  };
}

function InsightCard({ insight, onDismiss }) {
  const navigate = useNavigate();
  const item = normalizeInsight(insight);
  const findingLabel = insight.finding_label || "";
  const actionLabel = item.action_label || "View insight";
  const openInsight = () => {
    if (!insight.action_url) return;
    onDismiss(insight, true);
    navigateToApp(navigate, insight.action_url);
  };
  const swipe = useHorizontalCardSwipe(() => onDismiss(insight, false));

  return (
    <div
      className={`hm-attention-card is-${item.priority} is-insight${insight.action_url ? " is-clickable" : ""}`}
      role={insight.action_url ? "link" : undefined}
      tabIndex={insight.action_url ? 0 : undefined}
      aria-label={insight.action_url ? actionLabel : undefined}
      style={{ "--hm-card-swipe-x": `${swipe.offset}px` }}
      {...swipe.handlers}
      onClick={event => { if (!swipe.consumeClick(event)) openInsight(); }}
      onKeyDown={event => {
        if (!insight.action_url || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        openInsight();
      }}
    >
      <div className="hm-attention-icon" aria-hidden="true">✦</div>
      <div className="hm-attention-main">
        <div className="hm-insight-finding-row">
          {findingLabel && <span className="hm-insight-finding">{findingLabel}</span>}
          <span className="hm-insight-module-list">
            {(insight.modules || []).map((mod, i) => {
              const mb = MODULE_BADGE[mod] || { label: mod, color: "#64748b" };
              return <span key={i} className="hm-attention-module">{mb.label}</span>;
            })}
          </span>
        </div>
        <div className="hm-attention-title-row">
          <span className="hm-attention-title">{item.title}</span>
          <PriorityBadge priority={item.priority} />
        </div>
        <p className="hm-attention-description">{item.description}</p>
      </div>
      <div className="hm-attention-actions">
        {insight.action_url && <span className="hm-card-open-chevron" aria-hidden="true">›</span>}
      </div>
    </div>
  );
}

function InsightsPanel({ focusRequested }) {
  const [insights, setInsights]     = useState(null);
  const [running, setRunning]       = useState(false);
  const [dismissed, setDismissed]   = useState(new Set());
  const [showAll, setShowAll]       = useState(false);

  useEffect(() => {
    dashboardApi.insights().then(setInsights).catch(() => setInsights([]));
  }, []);

  useEffect(() => {
    if (!focusRequested || !insights) return;
    const visible = (insights || []).filter(i => !dismissed.has(i.id));
    if (visible.length === 0) return;
    window.setTimeout(() => {
      document.getElementById("dashboard-intelligence")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  }, [dismissed, focusRequested, insights]);

  const handleDismiss = useCallback(async (insight, wasActed) => {
    setDismissed(prev => new Set([...prev, insight.id]));
    try { await dashboardApi.dismissInsight(insight.id, wasActed); } catch {}
  }, []);

  const handleRunEngine = useCallback(async () => {
    setRunning(true);
    try {
      await dashboardApi.runEngine();
      const fresh = await dashboardApi.insights();
      setInsights(fresh);
      setDismissed(new Set());
    } catch {}
    setRunning(false);
  }, []);

  if (!insights) return null; // loading - don't render section yet
  const visible = (insights || []).filter(i => !dismissed.has(i.id));
  if (visible.length === 0) return null;

  const sortedVisible = sortByPriority(visible.map(normalizeInsight));
  const displayed = showAll ? sortedVisible : sortedVisible.slice(0, 2);

  return (
    <section id="dashboard-intelligence" style={{ marginTop: 28 }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12,
      }}>
        <div>
          <p style={{
            margin: "0 0 2px",
            color: "#047857",
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>
            ✨ HouseMind noticed
          </p>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--hm-text)" }}>
            🔗 Cross-module insights
          </h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {visible.length > 2 && (
            <button
              onClick={() => setShowAll(v => !v)}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--hm-cyan)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              {showAll ? "Show less ^" : `Show all ${visible.length} v`}
            </button>
          )}
          <button
            onClick={handleRunEngine}
            disabled={running}
            title="Refresh intelligence insights"
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: running ? "var(--hm-muted)" : "#72f2bf",
              background: "transparent",
              border: "1px solid rgba(114,242,191,0.3)",
              borderRadius: 8,
              cursor: running ? "default" : "pointer",
              padding: "4px 10px",
              letterSpacing: "0.04em",
            }}
          >
            {running ? "..." : "Refresh"}
          </button>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {displayed.map(item => (
          <InsightCard key={item.item_id} insight={item.original} onDismiss={handleDismiss} />
        ))}
      </div>
    </section>
  );
}

function PredictionsPanel() {
  const navigate = useNavigate();
  const [predictions, setPredictions] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    try {
      const payload = force
        ? await dashboardApi.refreshPredictions()
        : await dashboardApi.predictions();
      setPredictions(Array.isArray(payload) ? payload : (payload?.predictions || []));
      try { setAccuracy(await dashboardApi.predictionAccuracy()); } catch { setAccuracy(null); }
    } catch {
      setPredictions([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const dismiss = useCallback(async (prediction) => {
    setPredictions(rows => (rows || []).filter(row => row.id !== prediction.id));
    try { await dashboardApi.dismissPrediction(prediction.id); } catch {}
  }, []);

  if (!predictions || predictions.length === 0) return null;
  const displayed = expanded ? predictions : [];
  const actCount = predictions.filter(item => item.status === "act").length;
  const prepareCount = predictions.filter(item => item.status === "prepare").length;
  const watchCount = predictions.filter(item => item.status === "watch").length;
  const money = value => Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

  return (
    <section className={`hm-dashboard-summary-card hm-predictions ${expanded ? "is-expanded" : "is-collapsed"}`} aria-label="Household predictions">
      <div className="hm-predictions__head">
        <div className="hm-predictions__summary">
          <span className="hm-dashboard-card__eyebrow">🔮 Looking ahead</span>
          <h2>🔮 {predictions.length} forecast{predictions.length === 1 ? "" : "s"}</h2>
          <div className="hm-predictions__counts">
            {!!actCount && <em className="is-act">{actCount} act</em>}
            {!!prepareCount && <em className="is-prepare">{prepareCount} prepare</em>}
            {!!watchCount && <em className="is-watch">{watchCount} watch</em>}
          </div>
          {accuracy && <small>{accuracy.status === "measured" ? `${accuracy.accuracy_percent}% measured accuracy` : accuracy.resolved_count ? `${accuracy.resolved_count} verified · learning` : "Learning from confirmed outcomes"}</small>}
        </div>
        <div className="hm-predictions__controls">
          <button type="button" className="hm-dashboard-details is-toggle" onClick={() => setExpanded(value => !value)} aria-expanded={expanded}>{expanded ? "Close" : "Details"}<b>{expanded ? "⌃" : "›"}</b></button>
        </div>
      </div>
      {expanded && <div className="hm-predictions__grid">
        {displayed.map(prediction => {
          const evidence = Array.isArray(prediction.evidence) ? prediction.evidence : [];
          const domain = {
            utility_bill_forecast: ["💡", "Utilities"],
            subscription_commitment_forecast: ["🔁", "Subscriptions"],
            pantry_expiry_forecast: ["🥬", "Food"],
            warranty_expiry_forecast: ["🏠", "Home Vault"],
            vehicle_service_forecast: ["🚗", "Vehicles"],
            hvac_runtime_forecast: ["🌡️", "Smart Home"],
            pet_care_outlook: ["🐾", "Pets"],
            property_coverage_outlook: ["🏡", "Property Profile"],
            home_care_workload_outlook: ["🧰", "Home Care"],
            emergency_safety_outlook: ["🛟", "Emergency & Safety"],
            household_budget_outlook: ["💰", "Household Budget"],
          }[prediction.prediction_type] || ["🔮", "Household"];
          return <article key={prediction.id} className={`is-${prediction.status}`}>
            <div className="hm-prediction__meta">
              <div className="hm-prediction__domain"><span>{prediction.status === "act" ? "Act" : prediction.status === "prepare" ? "Prepare" : "Watch"}</span><b>{domain[0]} {domain[1]}</b></div>
              <em>{prediction.confidence_label} confidence</em>
            </div>
            <h3>{prediction.title}</h3>
            <p>{prediction.summary}</p>
            {(prediction.predicted_value != null || prediction.forecast_date) && <div className="hm-prediction__forecast">
              {prediction.predicted_value != null && <strong>{prediction.unit === "USD" ? money(prediction.predicted_value) : `${Number(prediction.predicted_value).toLocaleString()} ${prediction.unit || ""}`}</strong>}
              {prediction.forecast_date && <span>Forecast for {new Date(`${prediction.forecast_date}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
            </div>}
            <details>
              <summary>Why HouseMind expects this</summary>
              <small>Method: {String(prediction.method || "").replaceAll("_", " ")}</small>
              <ul>{evidence.slice(0, 6).map((item, index) => <li key={`${item.record_id || item.label}-${index}`}><b>{item.label}</b>{item.value != null ? ` · ${item.unit === "USD" ? money(item.value) : `${item.value}${item.unit ? ` ${item.unit}` : ""}`}` : ""}{item.date ? ` · ${new Date(`${item.date}T12:00:00`).toLocaleDateString()}` : ""}</li>)}</ul>
              {prediction.recommendation && <p>{prediction.recommendation}</p>}
            </details>
            <div className="hm-prediction__actions">
              {prediction.action_url && <button type="button" onClick={() => navigateToApp(navigate, prediction.action_url)}>Review</button>}
              <button type="button" onClick={() => dismiss(prediction)}>Dismiss</button>
            </div>
          </article>;
        })}
      </div>}
      {expanded && <p className="hm-predictions__note">Forecasts use confirmed household records and deterministic calculations. They are estimates, not posted bills or automatic actions.</p>}
      {expanded && <div className="hm-predictions__footer"><button type="button" onClick={() => load(true)} disabled={refreshing}>{refreshing ? "Refreshing forecasts…" : "↻ Refresh forecasts"}</button></div>}
    </section>
  );
}

function HouseholdActivityBar({ discoveryCount, discoveryLoading, cases, caseCount, casesLoading, graph, graphLoading, onDiscovery, onCases, onGraph, casesOpen, graphOpen }) {
  const entityCount = Number(graph?.summary?.entity_count || 0);
  const activeCaseCount = Number(caseCount ?? cases.length ?? 0);
  const attentionCount = Number(discoveryCount || 0) + activeCaseCount;
  return (
    <section className={`hm-dashboard-summary-card hm-activity-bar${attentionCount ? " has-attention" : ""}`} aria-label="HouseMind activity">
      <div className="hm-activity-bar__head">
        <div><span>HouseMind activity</span><strong>Discovery, decisions, and verified context</strong></div>
        <em>{attentionCount ? `${attentionCount} item${attentionCount === 1 ? "" : "s"} need${attentionCount === 1 ? "s" : ""} attention` : "All caught up"}</em>
      </div>
      <div className="hm-activity-bar__items">
        <button type="button" className={discoveryCount ? "needs-attention" : ""} onClick={onDiscovery}>
          <span>✦</span><div><strong>Discoveries</strong><small>{discoveryLoading ? "Checking…" : discoveryCount ? `${discoveryCount} to confirm` : "Nothing waiting"}</small></div><b>›</b>
        </button>
        <button type="button" className={`${activeCaseCount ? "needs-attention" : ""} ${activeCaseCount && casesOpen ? "is-open" : ""}`} onClick={onCases} disabled={casesLoading || !activeCaseCount}>
          <span>◇</span><div><strong>Cases</strong><small>{casesLoading ? "Checking…" : activeCaseCount ? `${activeCaseCount} need action` : "No open issues"}</small></div><b>{activeCaseCount ? (casesOpen ? "⌃" : "⌄") : "✓"}</b>
        </button>
        <button type="button" className={graphOpen ? "is-open" : ""} onClick={onGraph} disabled={graphLoading}>
          <span>⌘</span><div><strong>Household graph</strong><small>{graphLoading ? "Building…" : entityCount ? `${entityCount} verified records` : "Build verified context"}</small></div><b>{graphOpen ? "⌃" : "⌄"}</b>
        </button>
      </div>
    </section>
  );
}

function HouseholdCases({ cases, summary, loading, selected, onSelect, onClose, onStatus, hidePanel = false }) {
  const navigate = useNavigate();
  const [actionBusy, setActionBusy] = useState(null);
  const [outcomeAmount, setOutcomeAmount] = useState("");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [resolutionProducts, setResolutionProducts] = useState(null);
  const [resolutionError, setResolutionError] = useState("");
  const [orderDraft, setOrderDraft] = useState({ product_name: "", retailer: "", product_url: "", order_number: "", expected_delivery: "" });
  const [completionDraft, setCompletionDraft] = useState({ notes: "", next_service_days: "90", warranty_months: "", replacement_equipment: false });
  useEffect(() => {
    setResolutionProducts(null);
    setResolutionError("");
    setOrderDraft({ product_name: "", retailer: "", product_url: "", order_number: "", expected_delivery: "" });
    setCompletionDraft({ notes: "", next_service_days: "90", warranty_months: "", replacement_equipment: false });
  }, [selected?.id]);
  const money = value => Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
  return <>
    {!hidePanel && (loading || cases.length > 0 || Number(summary?.verified_count || 0) > 0) && <section className={`hm-cases-panel ${cases.length ? "has-cases" : "is-empty"}`}>
      <div className="hm-cases-head"><div><span>🗂️ HouseMind Cases</span><h2>{cases.length ? `${cases.length} confirmed issue${cases.length === 1 ? " needs" : "s need"} action` : "No confirmed issues need action"}</h2></div><em>Evidence-backed</em></div>
      {loading ? <div className="hm-cases-empty"><span className="spinner" /> Checking open Cases…</div> : cases.length ? <div className="hm-cases-grid">{cases.slice(0, 3).map(item => {
        const impact = item.financial_impact || {};
        return <button key={item.id} className="hm-case-card" onClick={() => onSelect(item)}>
          <span>{item.case_type.replaceAll("_", " ")}</span><strong>{item.title}</strong>
          <p>{item.summary}</p>
          {impact.annual_delta != null && <em>{money(impact.annual_delta)} annual impact</em>}
          {impact.difference != null && <em>{money(impact.difference)} bill increase · {impact.increase_percent}%</em>}
          {item.case_type === "home_repair" && <em>{impact.warranty_status === "active" ? "Warranty appears active" : impact.warranty_status === "expired" ? "Warranty appears expired" : "Coverage needs confirmation"}{impact.estimated_cost != null ? ` · ${money(impact.estimated_cost)} estimate` : ""}</em>}
          {item.case_type === "financial_anomaly" && <em>{impact.anomaly_type === "duplicate_charge" ? `${impact.duplicate_count || 2} matching charges · ${money(impact.amount)}` : `${money(impact.difference)} increase · ${impact.increase_percent}%`}</em>}
          {item.case_type === "trip_readiness" && <em>{impact.readiness_score}% ready · {impact.check_count} item{impact.check_count === 1 ? "" : "s"} to review</em>}
          <b>Review Case →</b>
        </button>;
      })}</div> : <p className="hm-cases-empty">When trusted records reveal a meaningful issue, HouseMind will assemble the evidence and next steps here.</p>}
      {Number(summary?.verified_count || 0) > 0 && <div className="hm-cases-verified">
        <div><span>Verified savings</span><strong>{money(summary.verified_monthly_savings)}<small>/month</small></strong><em>{money(summary.verified_annual_savings)} annualized across {summary.verified_count} verified Case{summary.verified_count === 1 ? "" : "s"}</em></div>
        <div className="hm-cases-verified__recent">{(summary.recent_verified || []).slice(0, 3).map(item => <button key={item.id} onClick={() => onSelect(item)}><strong>{item.title}</strong><span>Later bill verified {money(item.outcome?.monthly_savings)}/month</span></button>)}</div>
      </div>}
    </section>}
    {selected && <div className="hm-case-backdrop" onMouseDown={onClose} role="presentation"><section className="hm-case-drawer" onMouseDown={event => event.stopPropagation()} aria-label="Case details">
      <div className="hm-case-drawer-head"><div><span>Open Case</span><h2>{selected.title}</h2></div><button onClick={onClose}>×</button></div>
      <p>{selected.summary}</p>
      {selected.case_type === "trip_readiness" ? <div className="hm-case-impact"><div><small>Readiness</small><strong>{selected.financial_impact?.readiness_score}%</strong></div><div><small>Needs review</small><strong>{selected.financial_impact?.check_count || 0}</strong></div></div> : selected.case_type === "utility_increase" ? <div className="hm-case-impact"><div><small>Bill increase</small><strong>{money(selected.financial_impact?.difference)}</strong></div><div><small>Change</small><strong>{Number(selected.financial_impact?.increase_percent || 0).toFixed(1)}%</strong></div></div> : selected.case_type === "home_repair" ? <div className="hm-case-impact"><div><small>Warranty</small><strong>{selected.financial_impact?.warranty_status === "active" ? "Appears active" : selected.financial_impact?.warranty_status === "expired" ? "Appears expired" : "Confirm coverage"}</strong></div><div><small>Estimate</small><strong>{selected.financial_impact?.estimated_cost != null ? money(selected.financial_impact.estimated_cost) : "Not provided"}</strong></div></div> : selected.case_type === "financial_anomaly" ? <div className="hm-case-impact"><div><small>{selected.financial_impact?.anomaly_type === "duplicate_charge" ? "Charge" : "Increase"}</small><strong>{money(selected.financial_impact?.amount ?? selected.financial_impact?.difference)}</strong></div><div><small>Finding</small><strong>{selected.financial_impact?.anomaly_type === "duplicate_charge" ? "Possible duplicate" : `${Number(selected.financial_impact?.increase_percent || 0).toFixed(1)}% higher`}</strong></div></div> : <div className="hm-case-impact"><div><small>Monthly impact</small><strong>{money(selected.financial_impact?.monthly_delta)}</strong></div><div><small>Annual impact</small><strong>{money(selected.financial_impact?.annual_delta)}</strong></div></div>}
      <h3>Evidence</h3>
      {(selected.evidence || []).map(evidence => <article className="hm-case-evidence" key={evidence.id}><strong>{evidence.label}</strong><span>{evidence.snapshot?.source_account || evidence.snapshot?.source || (evidence.evidence_type === "contextual_signals" ? "Context, not causal proof" : "Connected source")}</span><p>{evidence.snapshot?.snippet || evidence.snapshot?.interpretation || (Array.isArray(evidence.snapshot?.checks) ? evidence.snapshot.checks.map(check => `${check.title}: ${check.message}`).join(" · ") : "The relevant extracted fields were retained with this Case.")}</p>{evidence.snapshot?.confidence != null && <small>{Math.round(Number(evidence.snapshot.confidence) * 100)}% extraction confidence</small>}{evidence.evidence_type === "contextual_signals" && <small>{evidence.snapshot?.weather?.available ? `Current outdoor context: ${evidence.snapshot.weather.temperature_f ?? "—"}°F` : "Weather context unavailable"} · {evidence.snapshot?.hvac_runtime?.available ? `${Math.round(Number(evidence.snapshot.hvac_runtime.today_runtime_min || 0))} min HVAC runtime today` : "HVAC runtime unavailable"}</small>}</article>)}
      <h3>Recommended actions</h3>
      {(selected.actions || []).map(action => <div className="hm-case-action" key={action.id}><div><strong>{action.title}</strong><span>{action.requires_approval ? "HouseMind will ask before taking this action." : "Available"}</span>{action.draft_content && <textarea readOnly rows="10" value={action.draft_content} />}</div>{action.action_type === "review_trip_readiness" ? <button onClick={() => navigate(action.destination || `/app/trip/event/${selected.subject_id}?tab=overview`)}>Open trip</button> : ["prepare_retention_request", "prepare_provider_question", "prepare_service_brief", "prepare_warranty_claim", "prepare_merchant_question"].includes(action.action_type) ? <button disabled={actionBusy === action.id} onClick={async () => { setActionBusy(action.id); try { if (action.draft_content) { await navigator.clipboard.writeText(action.draft_content); } else { const result = await casesApi.prepareAction(selected.id, action.id); onSelect(result.case); } } finally { setActionBusy(null); } }}>{actionBusy === action.id ? "Working…" : action.draft_content ? "Copy draft" : "Prepare"}</button> : <em>{action.status}</em>}</div>)}
      {selected.case_type === "home_repair" && <section className="hm-case-resolution">
        <div className="hm-case-resolution__head"><div><span>Closed-loop resolution</span><strong>{String(selected.outcome?.resolution_phase || "Find the right replacement").replaceAll("_", " ")}</strong></div><em>{selected.outcome?.order_number ? `Order ${selected.outcome.order_number}` : "Approval required"}</em></div>
        {!selected.outcome?.order_number && !resolutionProducts && <button className="hm-case-resolution__primary" disabled={actionBusy === "products"} onClick={async () => {
          setActionBusy("products"); setResolutionError("");
          try { setResolutionProducts(await casesApi.resolutionProducts(selected.id)); }
          catch (error) { setResolutionError(error?.message || "Replacement search is unavailable."); }
          finally { setActionBusy(null); }
        }}>{actionBusy === "products" ? "Matching equipment…" : "Find compatible replacements"}</button>}
        {resolutionError && <p className="hm-case-resolution__error">{resolutionError}</p>}
        {resolutionProducts && !selected.outcome?.order_number && <>
          <p className="hm-case-resolution__notice">{resolutionProducts.notice}</p>
          <div className="hm-case-resolution__products">{resolutionProducts.products.map(product => <article key={product.seller}><div><strong>{product.seller}</strong><span>{resolutionProducts.query}</span></div><a href={product.url} target="_blank" rel="noreferrer">Open search ↗</a><button type="button" onClick={() => setOrderDraft(draft => ({ ...draft, retailer: product.seller, product_name: resolutionProducts.query, product_url: product.url }))}>Track this retailer</button></article>)}</div>
          <form className="hm-case-resolution__form" onSubmit={async event => {
            event.preventDefault(); setActionBusy("order"); setResolutionError("");
            try { onSelect(await casesApi.recordResolutionOrder(selected.id, orderDraft)); }
            catch (error) { setResolutionError(error?.message || "The order could not be recorded."); }
            finally { setActionBusy(null); }
          }}><strong>Record an order you approved</strong><span>HouseMind does not buy anything. The exact order number lets connected email update this Case safely.</span><div><input required placeholder="Product or part" value={orderDraft.product_name} onChange={event => setOrderDraft(draft => ({ ...draft, product_name: event.target.value }))} /><input required placeholder="Retailer" value={orderDraft.retailer} onChange={event => setOrderDraft(draft => ({ ...draft, retailer: event.target.value }))} /><input required placeholder="Order number" value={orderDraft.order_number} onChange={event => setOrderDraft(draft => ({ ...draft, order_number: event.target.value }))} /><input type="date" aria-label="Expected delivery" value={orderDraft.expected_delivery} onChange={event => setOrderDraft(draft => ({ ...draft, expected_delivery: event.target.value }))} /></div><button disabled={actionBusy === "order"}>{actionBusy === "order" ? "Saving order…" : "Start order tracking"}</button></form>
        </>}
        {selected.outcome?.order_number && selected.status !== "resolved" && <div className="hm-case-resolution__tracking"><div><span className="is-done">Ordered</span><span className={["shipped", "awaiting_installation", "delivered", "completed"].includes(selected.outcome.resolution_phase) ? "is-done" : ""}>Shipped</span><span className={["awaiting_installation", "delivered", "completed"].includes(selected.outcome.resolution_phase) ? "is-done" : ""}>Delivered</span><span className={selected.outcome.resolution_phase === "completed" ? "is-done" : ""}>Installed</span></div>{selected.outcome.tracking_number && <small>Tracking: {selected.outcome.tracking_number}</small>}
          {selected.outcome.resolution_phase === "ordered" && <button onClick={async () => { setActionBusy("shipped"); try { onSelect(await casesApi.updateResolutionStatus(selected.id, { phase: "shipped" })); } finally { setActionBusy(null); } }}>Mark shipped manually</button>}
          {["shipped", "ordered"].includes(selected.outcome.resolution_phase) && <button onClick={async () => { setActionBusy("delivered"); try { onSelect(await casesApi.updateResolutionStatus(selected.id, { phase: "delivered" })); } finally { setActionBusy(null); } }}>Mark delivered</button>}
          {selected.outcome.resolution_phase === "awaiting_installation" && <form className="hm-case-resolution__form" onSubmit={async event => { event.preventDefault(); setActionBusy("complete"); try { onSelect(await casesApi.updateResolutionStatus(selected.id, { ...completionDraft, phase: "installed" })); } finally { setActionBusy(null); } }}><strong>Confirm installation</strong><span>This closes the Case, updates Home Vault maintenance history, and schedules the next service.</span><div><input type="number" min="1" max="3650" required aria-label="Next service interval in days" value={completionDraft.next_service_days} onChange={event => setCompletionDraft(draft => ({ ...draft, next_service_days: event.target.value }))} /><input type="number" min="0" max="240" aria-label="Warranty months" placeholder="Warranty months" value={completionDraft.warranty_months} onChange={event => setCompletionDraft(draft => ({ ...draft, warranty_months: event.target.value }))} /></div><textarea rows="3" placeholder="Installation or service notes" value={completionDraft.notes} onChange={event => setCompletionDraft(draft => ({ ...draft, notes: event.target.value }))} /><label><input type="checkbox" checked={completionDraft.replacement_equipment} onChange={event => setCompletionDraft(draft => ({ ...draft, replacement_equipment: event.target.checked }))} /> This replaced the complete equipment, not only a part</label><button disabled={actionBusy === "complete"}>{actionBusy === "complete" ? "Closing the loop…" : "Confirm installed and close Case"}</button></form>}
        </div>}
        {selected.status === "resolved" && selected.outcome?.resolution_phase === "completed" && <div className="hm-case-resolution__complete"><strong>Resolution verified</strong><span>Completed {selected.outcome.completed_on}. Next service: {selected.outcome.next_service_date}.</span></div>}
      </section>}
      {selected.case_type === "subscription_increase" && selected.outcome?.verification_status !== "verified" && <form className="hm-case-outcome" onSubmit={async event => {
        event.preventDefault(); setActionBusy("outcome");
        try { const result = await casesApi.recordOutcome(selected.id, { agreed_amount: outcomeAmount, notes: outcomeNotes }); onSelect(result); setOutcomeAmount(""); setOutcomeNotes(""); }
        finally { setActionBusy(null); }
      }}>
        <strong>Record the provider outcome</strong>
        <span>Enter the rate you accepted. HouseMind will call the savings verified only after a later confirmed bill matches it.</span>
        <div><label><span>Agreed price</span><input type="number" min="0" step="0.01" required value={outcomeAmount} onChange={event => setOutcomeAmount(event.target.value)} placeholder="0.00" /></label><label><span>Notes</span><input value={outcomeNotes} onChange={event => setOutcomeNotes(event.target.value)} placeholder="Offer or plan details" /></label></div>
        <button disabled={actionBusy === "outcome"}>{actionBusy === "outcome" ? "Saving…" : "Save and await bill verification"}</button>
      </form>}
      {selected.outcome?.verification_status && <div className={`hm-case-verification is-${selected.outcome.verification_status}`}><strong>{selected.outcome.verification_status === "verified" ? "Savings verified" : "Awaiting a later bill"}</strong><span>{money(selected.outcome.monthly_savings)} monthly · {money(selected.outcome.annual_savings)} annually</span></div>}
      <p className="hm-case-safety">HouseMind has not contacted the provider or taken a financial action.</p>
      <div className="hm-case-buttons"><button onClick={() => onStatus(selected, "dismissed")}>Not an issue</button><button onClick={() => onStatus(selected, "resolved")}>Mark resolved</button></div>
    </section></div>}
  </>;
}

function HouseholdGraphSnapshot({ graph, loading, onClose, onRefresh }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  if (loading) return <section className="hm-graph-panel"><div className="hm-cases-empty"><span className="spinner" /> Building confirmed household context…</div></section>;
  if (!graph?.summary?.entity_count) return null;
  const entities = graph.entities || [];
  const byId = Object.fromEntries(entities.map(entity => [entity.id, entity]));
  const types = [...new Set(entities.filter(entity => entity.entity_type !== "household").map(entity => entity.entity_type))];
  const root = entities.find(entity => entity.entity_type === "household");
  const filtered = entities.filter(entity => entity.entity_type === "household" || filter === "all" || entity.entity_type === filter).slice(0, 24);
  const positions = Object.fromEntries(filtered.map((entity, index) => {
    if (entity.entity_type === "household") return [entity.id, { x: 50, y: 50 }];
    const ringIndex = root ? index - 1 : index;
    const total = Math.max(filtered.length - (root ? 1 : 0), 1);
    const angle = (ringIndex / total) * Math.PI * 2 - Math.PI / 2;
    const radiusX = window.innerWidth < 560 ? 37 : 41;
    const radiusY = window.innerWidth < 560 ? 39 : 38;
    return [entity.id, { x: 50 + Math.cos(angle) * radiusX, y: 50 + Math.sin(angle) * radiusY }];
  }));
  const edges = (graph.relationships || []).filter(edge => positions[edge.from_entity_id] && positions[edge.to_entity_id]);
  const selected = byId[selectedId] || root || filtered[0];
  const selectedEdges = (graph.relationships || []).filter(edge => edge.from_entity_id === selected?.id || edge.to_entity_id === selected?.id);
  const sync = async () => {
    setSyncing(true);
    try { await householdGraphApi.sync(); await onRefresh?.(); }
    finally { setSyncing(false); }
  };
  return <div className="hm-graph-overlay" role="dialog" aria-modal="true" aria-label="Household graph" onClick={event => event.target === event.currentTarget && onClose?.()}>
    <section className="hm-graph-panel hm-graph-detail">
      <div className="hm-graph-head"><div><span>🔐 Verified household context</span><h2>Your home, connected with proof</h2><p>HouseMind uses these trusted links to build Cases, daily briefs and cross-module recommendations.</p></div><button className="hm-graph-close" onClick={onClose} aria-label="Close">×</button></div>
      <div className={`hm-graph-stats${graph.summary.needs_review_count ? " has-review" : ""}`}>
        <div title="Saved records, providers, people and other verified context HouseMind recognizes."><strong>{graph.summary.entity_count}</strong><span>Known items</span><small>Verified household context</small></div>
        <div title="Confirmed relationships between known items, such as an account being provided by a utility company."><strong>{graph.summary.relationship_count}</strong><span>Connections</span><small>Trusted relationships</small></div>
        <div title="Saved module records and approved discoveries supporting the known items."><strong>{entities.reduce((sum, item) => sum + Number(item.evidence_count || 0) + Number(Boolean(item.record_verified)), 0)}</strong><span>Sources</span><small>Records and approvals</small></div>
        {graph.summary.needs_review_count > 0 && <div className="is-review"><strong>{graph.summary.needs_review_count}</strong><span>Needs review</span><small>Confirm or correct</small></div>}
      </div>
      <div className="hm-graph-toolbar">
        <div><button className={filter === "all" ? "is-active" : ""} onClick={() => setFilter("all")}>All</button>{types.map(type => <button key={type} className={filter === type ? "is-active" : ""} onClick={() => setFilter(type)}>{titleCase(type.replaceAll("_", " "))}</button>)}</div>
        <button className="hm-graph-sync" disabled={syncing} onClick={sync}>{syncing ? "Refreshing…" : "Refresh verified context"}</button>
      </div>
      <div className="hm-graph-workspace">
        <div className="hm-graph-canvas">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">{edges.map(edge => <line key={edge.id} x1={positions[edge.from_entity_id].x} y1={positions[edge.from_entity_id].y} x2={positions[edge.to_entity_id].x} y2={positions[edge.to_entity_id].y} />)}</svg>
          {filtered.map(entity => <button key={entity.id} className={`hm-graph-node is-${entity.entity_type}${selected?.id === entity.id ? " is-selected" : ""}`} style={{ left: `${positions[entity.id].x}%`, top: `${positions[entity.id].y}%` }} onClick={() => setSelectedId(entity.id)}><i>{entity.entity_type === "household" ? "⌂" : entity.entity_type === "person" ? "●" : entity.entity_type === "vehicle" ? "◆" : entity.entity_type === "trip" ? "✈" : entity.entity_type === "utility_account" ? "↯" : entity.entity_type === "contract" ? "$" : entity.entity_type === "pantry_item" ? "◌" : "◇"}</i><strong>{entity.name}</strong><span>{entity.module_label}</span></button>)}
        </div>
        {selected && <aside className="hm-graph-inspector"><span>{titleCase(selected.entity_type.replaceAll("_", " "))}</span><h3>{selected.name}</h3><div className={`hm-graph-proof${selected.status === "needs_review" ? " is-review" : ""}`}><b>{selected.status === "needs_review" ? "! Needs review" : "✓ Confirmed"}</b><small>{selected.source === "saved_record" ? `Saved in ${selected.module_label}` : selected.status === "needs_review" ? "HouseMind needs your confirmation" : `Approved from ${selected.source}`}</small></div>
          {Object.keys(selected.attributes || {}).length > 0 && <dl>{Object.entries(selected.attributes).slice(0, 6).map(([key, value]) => <div key={key}><dt>{titleCase(key.replaceAll("_", " "))}</dt><dd>{String(value)}</dd></div>)}</dl>}
          <div className="hm-graph-related"><strong>Connections</strong>{selectedEdges.length ? selectedEdges.slice(0, 8).map(edge => { const other = byId[edge.from_entity_id === selected.id ? edge.to_entity_id : edge.from_entity_id]; return <button key={edge.id} onClick={() => setSelectedId(other?.id)}><span>{String(edge.relationship_type).replaceAll("_", " ")}</span><b>{other?.name || "Unknown"}</b></button>; }) : <small>No verified relationship yet.</small>}</div>
          <div className="hm-graph-evidence"><strong>Proof</strong>{selected.canonical_record_type && <span>✓ Saved HouseMind record</span>}{(selected.evidence || []).map(item => <span key={item.id}>✓ {item.subject || `${item.source} confirmation`}</span>)}</div>
          {selected.action_url && <button className="hm-graph-open-record" onClick={() => navigate(selected.action_url)}>{selected.status === "needs_review" ? `Review in ${selected.module_label}` : `Open in ${selected.module_label}`} →</button>}
        </aside>}
      </div>
      {entities.length > 24 && filter === "all" && <p className="hm-graph-limit">Showing 24 of {entities.length} verified records. Choose a category to inspect the rest.</p>}
    </section>
  </div>;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusIntelligence = searchParams.get("intelligence") === "refreshed";
  const reviewTarget = searchParams.get("review");
  const linkedDiscoveryId = Number(searchParams.get("discovery") || 0) || null;
  const discoveryViewRequested = searchParams.get("view") === "discoveries";
  const linkedCaseId = Number(searchParams.get("case") || 0) || null;
  const connectorSetupStatus = searchParams.get("connector") === "setup" ? (searchParams.get("status") || "pending") : null;
  const dailyBriefRequested = searchParams.get("daily_brief") === "1";
  const today = new Date();
  const dateLabel = today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  const [briefing, setBriefing]         = useState(null);
  const [localWeather, setLocalWeather] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(true);
  const [attentionItems, setAttentionItems]   = useState([]);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [dismissed, setDismissed]       = useState(new Set());
  const [dismissTarget, setDismissTarget] = useState(null);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [bulkAttentionGroup, setBulkAttentionGroup] = useState(null);
  const [bulkAttentionSelected, setBulkAttentionSelected] = useState(new Set());
  const [bulkAttentionBusy, setBulkAttentionBusy] = useState(false);
  const [bulkAttentionError, setBulkAttentionError] = useState("");
  const [todayTasks, setTodayTasks] = useState([]);
  const [todayTasksLoading, setTodayTasksLoading] = useState(true);
  const [todayTaskDraft, setTodayTaskDraft] = useState("");
  const [todayTaskBusyId, setTodayTaskBusyId] = useState(null);
  const [showAllTodayTasks, setShowAllTodayTasks] = useState(false);
  const [todayTasksOpen, setTodayTasksOpen] = useState(false);
  const [todayTaskDecision, setTodayTaskDecision] = useState(null);
  const [refreshingBriefing, setRefreshingBriefing] = useState(false);
  const [briefingOpen, setBriefingOpen]             = useState(() => (
    dailyBriefRequested || (new Date().getHours() < 12 && !wasBriefingDismissedToday())
  ));
  const [briefingOverlayMode, setBriefingOverlayMode] = useState(() => (
    typeof window !== "undefined" && window.matchMedia(DAILY_BRIEF_OVERLAY_QUERY).matches
  ));
  const [briefingDay, setBriefingDay] = useState(() => localISODate(new Date()));
  const heroRef = useRef(null);
  const briefingPanelRef = useRef(null);
  const briefingSwipeRef = useRef(null);
  const briefingCloseTimerRef = useRef(null);
  const [briefingSwipeOffset, setBriefingSwipeOffset] = useState(0);
  const [briefingClosing, setBriefingClosing] = useState(false);
  const [importCount, setImportCount] = useState(0);
  const [importCountLoading, setImportCountLoading] = useState(true);
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [focusedImportId, setFocusedImportId] = useState(null);
  const [importCandidates, setImportCandidates] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [importHistoryMeta, setImportHistoryMeta] = useState(null);
  const [importHistoryFilters, setImportHistoryFilters] = useState({ status: "", source: "", source_account: "", module: "", dismissal_reason: "" });
  const [importSourcesPayload, setImportSourcesPayload] = useState(null);
  const [trustedImportRules, setTrustedImportRules] = useState([]);
  const [trustedImportRulesLoading, setTrustedImportRulesLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importHistoryLoading, setImportHistoryLoading] = useState(false);
  const [importSourcesLoading, setImportSourcesLoading] = useState(false);
  const [importSourceBusyId, setImportSourceBusyId] = useState(null);
  const [importBusyId, setImportBusyId] = useState(null);
  const [importConfirmation, setImportConfirmation] = useState(null);
  const [importNotice, setImportNotice] = useState("");
  const [importError, setImportError] = useState("");
  const [importIntakeOpen, setImportIntakeOpen] = useState(false);
  const [importHistoryOpen, setImportHistoryOpen] = useState(false);
  const [importSourcesOpen, setImportSourcesOpen] = useState(false);
  const [importIntakeBusy, setImportIntakeBusy] = useState(false);
  const [importIntakeFileBusy, setImportIntakeFileBusy] = useState(false);
  const [importIntakeDraft, setImportIntakeDraft] = useState({
    intake_type: "email",
    provider: "manual",
    source_account: "",
    subject: "",
    body: "",
  });
  const [openCases, setOpenCases] = useState([]);
  const [caseCount, setCaseCount] = useState(0);
  const [casesLoading, setCasesLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseSummary, setCaseSummary] = useState(null);
  const [householdGraph, setHouseholdGraph] = useState(null);
  const [householdGraphLoading, setHouseholdGraphLoading] = useState(false);
  const [householdCasesOpen, setHouseholdCasesOpen] = useState(false);
  const [householdGraphOpen, setHouseholdGraphOpen] = useState(false);
  const connectionAlerts = useMemo(() => (importSourcesPayload?.sources || [])
    .map(source => ({ source, reason: connectionAttentionReason(source) }))
    .filter(item => item.reason), [importSourcesPayload]);

  const clearLinkedParam = useCallback((key) => {
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      next.delete(key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeImportDrawer = useCallback(() => {
    setImportDrawerOpen(false);
    setFocusedImportId(null);
    if (linkedDiscoveryId) clearLinkedParam("discovery");
    if (discoveryViewRequested) clearLinkedParam("view");
  }, [clearLinkedParam, discoveryViewRequested, linkedDiscoveryId]);

  const closeSelectedCase = useCallback(() => {
    setSelectedCase(null);
    if (linkedCaseId) clearLinkedParam("case");
  }, [clearLinkedParam, linkedCaseId]);

  useEffect(() => {
    if (!focusIntelligence) return undefined;
    const timer = setTimeout(() => {
      setSearchParams(params => {
        const next = new URLSearchParams(params);
        next.delete("intelligence");
        return next;
      }, { replace: true });
    }, 1200);
    return () => clearTimeout(timer);
  }, [focusIntelligence, setSearchParams]);

  const loadBriefing = useCallback(async (force = false) => {
    setBriefingLoading(true);
    try {
      const data = await dashboardApi.briefing(force);
      setBriefing(data);
    } catch {
    } finally {
      setBriefingLoading(false);
    }
  }, []);

  const loadLocalWeather = useCallback(async () => {
    try {
      const data = await dashboardApi.currentWeather();
      setLocalWeather(data?.available ? data : null);
    } catch {
      setLocalWeather(null);
    }
  }, []);

  const loadAttentionItems = useCallback(async () => {
    setAttentionLoading(true);
    try {
      const items = await dashboardApi.attentionItems();
      setAttentionItems(Array.isArray(items) ? items : []);
    } catch {
    } finally {
      setAttentionLoading(false);
    }
  }, []);

  const loadDashboardBootstrap = useCallback(async (force = false) => {
    setBriefingLoading(true);
    setAttentionLoading(true);
    setImportCountLoading(true);
    try {
      const data = await dashboardApi.bootstrap(force);
      setBriefing(data?.briefing || null);
      setAttentionItems(Array.isArray(data?.attention_items) ? data.attention_items : []);
      setImportCount(Number(data?.activity?.discovery_count || 0));
      setCaseCount(Number(data?.activity?.case_count || 0));
      setHouseholdGraph({ summary: { entity_count: Number(data?.activity?.entity_count || 0) } });
      window.dispatchEvent(new CustomEvent("hm_dashboard_bootstrap", { detail: data }));
    } catch {
      setAttentionItems([]);
      setImportCount(0);
      setCaseCount(0);
    } finally {
      setBriefingLoading(false);
      setAttentionLoading(false);
      setImportCountLoading(false);
    }
  }, []);

  const loadTodayTasks = useCallback(async () => {
    setTodayTasksLoading(true);
    try {
      const items = await dashboardApi.todayTasks();
      setTodayTasks(Array.isArray(items) ? items : []);
    } catch {
      setTodayTasks([]);
    } finally {
      setTodayTasksLoading(false);
    }
  }, []);

  const loadImportCount = useCallback(async () => {
    setImportCountLoading(true);
    try {
      const data = await importCandidatesApi.count();
      setImportCount(Number(data?.pending_count || 0));
    } catch {
      setImportCount(0);
    } finally {
      setImportCountLoading(false);
    }
  }, []);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try { await casesApi.refreshTripReadiness().catch(() => null); const [active, summary] = await Promise.all([casesApi.list("active"), casesApi.summary()]); setOpenCases(active); setCaseCount(Array.isArray(active) ? active.length : 0); setCaseSummary(summary); }
    catch { setOpenCases([]); setCaseSummary(null); }
    finally { setCasesLoading(false); }
  }, []);

  const loadHouseholdGraph = useCallback(async () => {
    setHouseholdGraphLoading(true);
    try { setHouseholdGraph(await householdGraphApi.get()); }
    catch { setHouseholdGraph(null); }
    finally { setHouseholdGraphLoading(false); }
  }, []);

  const openHouseholdGraph = useCallback(async () => {
    setHouseholdGraphOpen(true);
    setHouseholdGraphLoading(true);
    try {
      await householdGraphApi.sync();
      setHouseholdGraph(await householdGraphApi.get());
    } catch {
      setHouseholdGraph(null);
    } finally { setHouseholdGraphLoading(false); }
  }, []);

  const updateCaseStatus = useCallback(async (item, status) => {
    try {
      await casesApi.updateStatus(item.id, { status });
      setSelectedCase(null);
      if (linkedCaseId) clearLinkedParam("case");
      await loadCases();
    } catch {}
  }, [clearLinkedParam, linkedCaseId, loadCases]);

  const loadImportCandidates = useCallback(async () => {
    setImportLoading(true);
    try {
      const data = await importCandidatesApi.list();
      setImportCandidates(Array.isArray(data) ? data : []);
      setImportCount(Array.isArray(data) ? data.length : 0);
    } catch (err) {
      setImportCandidates([]);
      setImportError(err?.message || "HouseMind could not load discovered items.");
    } finally {
      setImportLoading(false);
    }
  }, []);

  const loadImportHistory = useCallback(async (filters = importHistoryFilters) => {
    setImportHistoryLoading(true);
    try {
      const data = await importCandidatesApi.history(filters);
      setImportHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setImportHistory([]);
      setImportError(err?.message || "HouseMind could not load discovery history.");
    } finally {
      setImportHistoryLoading(false);
    }
  }, [importHistoryFilters]);

  const loadImportHistoryMeta = useCallback(async () => {
    try {
      const data = await importCandidatesApi.historyMeta();
      setImportHistoryMeta(data || null);
    } catch {
      setImportHistoryMeta(null);
    }
  }, []);

  useEffect(() => {
    if (!linkedCaseId) return undefined;
    let cancelled = false;
    setHouseholdCasesOpen(true);
    casesApi.get(linkedCaseId).then(item => {
      if (!cancelled) setSelectedCase(item);
    }).catch(() => {
      if (!cancelled) clearLinkedParam("case");
    });
    return () => { cancelled = true; };
  }, [clearLinkedParam, linkedCaseId]);

  useEffect(() => {
    if (!linkedDiscoveryId) return undefined;
    let cancelled = false;
    const openLinkedDiscovery = async () => {
      setImportDrawerOpen(true);
      setFocusedImportId(linkedDiscoveryId);
      const [pending, history] = await Promise.all([
        importCandidatesApi.list().catch(() => []),
        importCandidatesApi.history().catch(() => []),
      ]);
      if (cancelled) return;
      setImportCandidates(Array.isArray(pending) ? pending : []);
      setImportCount(Array.isArray(pending) ? pending.length : 0);
      setImportHistory(Array.isArray(history) ? history : []);
      const found = [...(pending || []), ...(history || [])].some(item => Number(item.id) === linkedDiscoveryId);
      if (!found) {
        setImportDrawerOpen(false);
        setFocusedImportId(null);
        clearLinkedParam("discovery");
        return;
      }
      const isHistorical = (history || []).some(item => Number(item.id) === linkedDiscoveryId);
      setImportHistoryOpen(isHistorical);
      window.setTimeout(() => document.getElementById(`discovery-${linkedDiscoveryId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    };
    openLinkedDiscovery();
    return () => { cancelled = true; };
  }, [clearLinkedParam, linkedDiscoveryId]);

  const loadImportSources = useCallback(async () => {
    setImportSourcesLoading(true);
    try {
      const data = await importCandidatesApi.sources();
      setImportSourcesPayload(data || null);
    } catch (err) {
      setImportSourcesPayload(null);
      setImportError(err?.message || "HouseMind could not load discovery sources.");
    } finally {
      setImportSourcesLoading(false);
    }
  }, []);

  const loadTrustedImportRules = useCallback(async () => {
    setTrustedImportRulesLoading(true);
    try {
      const data = await importCandidatesApi.trustedRules();
      setTrustedImportRules(Array.isArray(data?.rules) ? data.rules : []);
    } catch {
      setTrustedImportRules([]);
    } finally {
      setTrustedImportRulesLoading(false);
    }
  }, []);

  const openImportDrawer = useCallback(async () => {
    setImportNotice("");
    setImportError("");
    setImportDrawerOpen(true);
    await Promise.all([loadImportCandidates(), loadTrustedImportRules()]);
  }, [loadImportCandidates, loadTrustedImportRules]);

  useEffect(() => {
    if (!discoveryViewRequested) return;
    openImportDrawer();
  }, [discoveryViewRequested, openImportDrawer]);

  useEffect(() => {
    if (!connectorSetupStatus) return undefined;
    const msg = connectorSetupStatus === "connected"
      ? "Email connector connected successfully. HouseMind will scan your inbox tonight and surface findings for review."
      : connectorSetupStatus === "error"
        ? "Connector setup failed or was cancelled. Check credentials and try again."
        : "Connector setup is pending. Open Sources to complete setup.";
    if (connectorSetupStatus === "connected") {
      setImportNotice(msg);
    } else {
      setImportError(msg);
    }
    setImportDrawerOpen(true);
    setImportSourcesOpen(true);
    loadImportSources();
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      next.delete("connector");
      next.delete("status");
      return next;
    }, { replace: true });
  }, [connectorSetupStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reviewTarget !== "sources") return undefined;
    let cancelled = false;
    const openSources = async () => {
      setImportDrawerOpen(true);
      setImportSourcesOpen(true);
      await Promise.all([loadImportCandidates(), loadImportSources()]);
      if (!cancelled) {
        setSearchParams(params => {
          const next = new URLSearchParams(params);
          next.delete("review");
          return next;
        }, { replace: true });
      }
    };
    openSources();
    return () => {
      cancelled = true;
    };
  }, [loadImportCandidates, loadImportSources, reviewTarget, setSearchParams]);

  const refreshImports = useCallback(async () => {
    setImportError("");
    await loadImportCandidates();
    await loadImportCount();
    if (importHistoryOpen) {
      await loadImportHistory();
      await loadImportHistoryMeta();
    }
  }, [importHistoryOpen, loadImportCandidates, loadImportCount, loadImportHistory, loadImportHistoryMeta]);

  const handleApproveImport = useCallback(async (candidate) => {
    setImportBusyId(candidate.id);
    setImportNotice("");
    setImportError("");
    try {
      const result = await importCandidatesApi.approve(candidate.id);
      setImportNotice(importSavedMessage(candidate, result));
      await refreshImports();
      loadCases();
      loadHouseholdGraph();
      loadAttentionItems();
      loadBriefing(true);
    } catch (err) {
      setImportError(err?.message || "HouseMind could not accept this item. Please try again.");
    } finally {
      setImportBusyId(null);
    }
  }, [loadAttentionItems, loadBriefing, loadCases, loadHouseholdGraph, refreshImports]);

  const approveAndTrustImport = useCallback(async (candidate, autoSafe) => {
    setImportBusyId(candidate.id);
    setImportNotice("");
    setImportError("");
    try {
      const result = await importCandidatesApi.approveAndTrust(candidate.id, {
        action: autoSafe ? "auto_import" : "suggest",
        minimum_confidence: 0.9,
      });
      setImportNotice(`${importSavedMessage(candidate, result)} Trusted rule saved.`);
      await Promise.all([refreshImports(), loadTrustedImportRules()]);
      loadCases();
      loadHouseholdGraph();
      loadAttentionItems();
      loadBriefing(true);
    } catch (err) {
      setImportError(err?.message || "HouseMind could not save this trusted rule.");
    } finally {
      setImportBusyId(null);
    }
  }, [loadAttentionItems, loadBriefing, loadCases, loadHouseholdGraph, loadTrustedImportRules, refreshImports]);

  const handleApproveAndTrustImport = useCallback((candidate) => {
    const autoSafe = ["events", "pantry", "vault", "vehicles"].includes(candidate.proposed_module);
    setImportConfirmation({
      tag: "✦ HouseMind confirmation",
      title: autoSafe ? "Automate future discoveries?" : "Remember this source?",
      message: autoSafe
        ? "HouseMind will import this item now, then automatically import only future high-confidence matches from this same account. You can turn this rule off anytime in Inbox controls."
        : "HouseMind will import this item and remember this exact source pattern. Financial changes will still return to your Discovery Inbox for review.",
      confirmLabel: autoSafe ? "Accept & automate" : "Accept & remember",
      cancelLabel: "Keep reviewing",
      danger: false,
      onConfirm: () => approveAndTrustImport(candidate, autoSafe),
    });
  }, [approveAndTrustImport]);

  const handleDisableTrustedImportRule = useCallback(async (rule) => {
    setImportError("");
    try {
      await importCandidatesApi.disableTrustedRule(rule.id);
      setImportNotice(`${titleCase(rule.provider_key)} will return to normal review.`);
      await loadTrustedImportRules();
    } catch (err) {
      setImportError(err?.message || "HouseMind could not turn off this rule.");
    }
  }, [loadTrustedImportRules]);

  const handleMergeImport = useCallback(async (candidate, match) => {
    setImportBusyId(candidate.id);
    setImportNotice("");
    setImportError("");
    try {
      const result = await importCandidatesApi.merge(candidate.id, {
        record_type: match?.record_type,
        record_id: match?.record_id,
      });
      setImportNotice(importSavedMessage(candidate, result, "updated the existing"));
      await refreshImports();
      loadCases();
      loadHouseholdGraph();
      loadAttentionItems();
      loadBriefing(true);
    } catch (err) {
      setImportError(err?.message || "HouseMind could not update the existing record.");
    } finally {
      setImportBusyId(null);
    }
  }, [loadAttentionItems, loadBriefing, loadCases, loadHouseholdGraph, refreshImports]);

  const mergeAndTrustImport = useCallback(async (candidate, match) => {
    setImportBusyId(candidate.id);
    setImportNotice("");
    setImportError("");
    try {
      const result = await importCandidatesApi.mergeAndTrust(candidate.id, {
        record_type: match?.record_type,
        record_id: match?.record_id,
        action: "auto_update",
        minimum_confidence: 0.95,
      });
      setImportNotice(`${importSavedMessage(candidate, result, "updated the existing")} Future statements from this provider will update it automatically when safe.`);
      await Promise.all([refreshImports(), loadTrustedImportRules()]);
      loadCases();
      loadHouseholdGraph();
      loadAttentionItems();
      loadBriefing(true);
    } catch (err) {
      setImportError(err?.message || "HouseMind could not save this trusted update rule.");
    } finally {
      setImportBusyId(null);
    }
  }, [loadAttentionItems, loadBriefing, loadCases, loadHouseholdGraph, loadTrustedImportRules, refreshImports]);

  const handleMergeAndTrustImport = useCallback((candidate, match) => {
    setImportConfirmation({
      tag: "✦ HouseMind confirmation",
      title: "Trust future statements?",
      message: "HouseMind will update this record now and automatically apply future high-confidence statements from this provider, including amount, due date, billing period, and autopay. New accounts or unusually large changes will still return for review, and every automatic update can be undone.",
      confirmLabel: "Update & trust",
      cancelLabel: "Keep reviewing",
      danger: false,
      onConfirm: () => mergeAndTrustImport(candidate, match),
    });
  }, [mergeAndTrustImport]);

  const handleResolveMergeImport = useCallback(async (candidate, match, resolutions) => {
    setImportBusyId(candidate.id);
    setImportNotice("");
    setImportError("");
    try {
      const result = await importCandidatesApi.resolveMerge(candidate.id, {
        record_type: match?.record_type,
        record_id: match?.record_id,
        resolutions,
      });
      setImportNotice(importSavedMessage(candidate, result, "resolved and updated"));
      await refreshImports();
      loadCases();
      loadHouseholdGraph();
      loadAttentionItems();
      loadBriefing(true);
    } catch (err) {
      setImportError(err?.message || "HouseMind could not apply the conflict decisions.");
    } finally {
      setImportBusyId(null);
    }
  }, [loadAttentionItems, loadBriefing, loadCases, loadHouseholdGraph, refreshImports]);

  const handleCorrectImport = useCallback(async (candidate, payload) => {
    setImportBusyId(candidate.id);
    setImportNotice("");
    setImportError("");
    try {
      await importCandidatesApi.correct(candidate.id, payload);
      setImportNotice(`${candidateTitle(candidate)} corrections saved. Review the updated details before importing.`);
      await refreshImports();
      return true;
    } catch (err) {
      setImportError(err?.message || "HouseMind could not save these corrections.");
      return false;
    } finally {
      setImportBusyId(null);
    }
  }, [refreshImports]);

  const handleDismissImport = useCallback(async (candidate, reason = "user_dismissed") => {
    setImportBusyId(candidate.id);
    setImportNotice("");
    setImportError("");
    try {
      await importCandidatesApi.dismiss(candidate.id, { reason });
      setImportNotice(reason === "duplicate" ? `${candidateTitle(candidate)} dismissed as duplicate.` : `${candidateTitle(candidate)} dismissed.`);
      await refreshImports();
    } catch (err) {
      setImportError(err?.message || "HouseMind could not dismiss this item. Please try again.");
    } finally {
      setImportBusyId(null);
    }
  }, [refreshImports]);

  const handleApproveHighConfidence = useCallback(async () => {
    const clearCandidates = importCandidates.filter(candidate =>
      Number(candidate.confidence || 0) >= 0.9 &&
      SUPPORTED_IMPORT_MODULES.has(candidate.proposed_module) &&
      !candidateIsPossibleDuplicate(candidate)
    );
    setImportBusyId("batch");
    setImportNotice("");
    setImportError("");
    try {
      await Promise.all(clearCandidates.map(candidate => importCandidatesApi.approve(candidate.id)));
      setImportNotice(`${clearCandidates.length} clear item${clearCandidates.length === 1 ? "" : "s"} imported. Possible duplicates were left for review.`);
      await refreshImports();
      loadAttentionItems();
      loadBriefing(true);
    } catch (err) {
      setImportError(err?.message || "HouseMind could not import the high confidence items.");
    } finally {
      setImportBusyId(null);
    }
  }, [importCandidates, loadAttentionItems, loadBriefing, refreshImports]);

  const updateImportIntakeDraft = useCallback((patch) => {
    setImportIntakeDraft(draft => ({ ...draft, ...patch }));
  }, []);

  const toggleImportHistory = useCallback(async () => {
    setImportHistoryOpen(open => !open);
    if (!importHistoryOpen) {
      await loadImportHistory(importHistoryFilters);
      await loadImportHistoryMeta();
    }
  }, [importHistoryFilters, importHistoryOpen, loadImportHistory, loadImportHistoryMeta]);

  const handleImportHistoryFilterChange = useCallback(async (patch) => {
    const next = { ...importHistoryFilters, ...patch };
    setImportHistoryFilters(next);
    await loadImportHistory(next);
  }, [importHistoryFilters, loadImportHistory]);

  const deleteImportHistory = useCallback(async (candidate) => {
    if (!candidate?.id || !["dismissed", "expired"].includes(candidate.status)) return;
    setImportNotice("");
    setImportError("");
    try {
      await importCandidatesApi.deleteHistoryItem(candidate.id);
      setImportNotice("Discovery removed from history.");
      await Promise.all([loadImportHistory(importHistoryFilters), loadImportHistoryMeta()]);
    } catch (err) {
      setImportError(err?.message || "HouseMind could not delete that discovery.");
    }
  }, [importHistoryFilters, loadImportHistory, loadImportHistoryMeta]);

  const handleDeleteImportHistory = useCallback((candidate) => {
    if (!candidate?.id || !["dismissed", "expired"].includes(candidate.status)) return;
    setImportConfirmation({
      tag: "⚠ HouseMind warning",
      title: "Delete this discovery?",
      message: "This permanently removes the item from Discovery history and cannot be undone.",
      confirmLabel: "Delete permanently",
      cancelLabel: "Keep it",
      danger: true,
      onConfirm: () => deleteImportHistory(candidate),
    });
  }, [deleteImportHistory]);

  const toggleImportSources = useCallback(async () => {
    setImportSourcesOpen(open => !open);
    if (!importSourcesOpen && !importSourcesPayload) {
      await loadImportSources();
    }
  }, [importSourcesOpen, importSourcesPayload, loadImportSources]);

  const handleImportSourceAction = useCallback(async (source, action, data = {}) => {
    if (!source?.id) return;
    setImportSourceBusyId(source.id);
    setImportNotice("");
    setImportError("");
    try {
      let updated;
      if (action === "request") {
        updated = await importCandidatesApi.requestConnection(source.id, { source_account: source.source_account || "" });
        if (updated?.oauth?.ready && updated.oauth.auth_url) {
          setImportNotice(`Opening ${source.label} authorization...`);
          window.location.assign(updated.oauth.auth_url);
        } else {
          setImportNotice(updated?.oauth?.message || `${source.label} setup request recorded. Connection details will be enabled when the provider integration is ready.`);
        }
      } else if (action === "configure_imap") {
        updated = await importCandidatesApi.configureImapConnection(source.id, data);
        setImportNotice(`${source.label} IMAP setup saved. HouseMind will review discovered items before importing anything.`);
      } else if (action === "disable") {
        updated = await importCandidatesApi.disableConnection(source.id);
        setImportNotice(`${source.label} disabled for discovery.`);
      } else if (action === "disconnect") {
        updated = await importCandidatesApi.disconnectConnection(source.id);
        setImportNotice(`${source.label} disconnected.`);
      } else if (action === "sync" || action === "recheck") {
        const result = await importCandidatesApi.syncConnection(source.id, { recheck: action === "recheck" });
        updated = result.connection;
        const stats = result.stats || {};
        setImportNotice(`${source.label} checked ${Number(stats.adapter_checked || 0)} item${Number(stats.adapter_checked || 0) === 1 ? "" : "s"} and created ${Number(stats.adapter_created || 0)} new review item${Number(stats.adapter_created || 0) === 1 ? "" : "s"}.`);
        await Promise.all([loadImportCandidates(), loadImportCount()]);
      }
      if (updated) {
        setImportSourcesPayload(payload => ({
          ...(payload || {}),
          sources: (payload?.sources || []).map(item => item.id === updated.id ? updated : item),
        }));
      }
    } catch (err) {
      setImportError(err?.message || "HouseMind could not update that discovery source.");
    } finally {
      setImportSourceBusyId(null);
    }
  }, [loadImportCandidates, loadImportCount]);

  const handleSubmitImportIntake = useCallback(async (event) => {
    event.preventDefault();
    setImportIntakeBusy(true);
    setImportNotice("");
    setImportError("");
    try {
      const isCalendar = importIntakeDraft.intake_type === "calendar";
      const result = isCalendar
        ? await importCandidatesApi.discoverCalendar({
            provider: importIntakeDraft.provider || "manual_calendar",
            source_account: importIntakeDraft.source_account,
            title: importIntakeDraft.subject,
            body: importIntakeDraft.body,
          })
        : await importCandidatesApi.discoverEmail(importIntakeDraft);
      const createdCount = Number(result?.created_count || 0);
      setImportNotice(createdCount > 0
        ? `HouseMind found ${createdCount} review item${createdCount === 1 ? "" : "s"} from those ${isCalendar ? "calendar details" : "email details"}.`
        : `HouseMind did not find reviewable household items in those ${isCalendar ? "calendar details" : "email details"}.`);
      setImportIntakeDraft(draft => ({ ...draft, subject: "", body: "" }));
      await refreshImports();
    } catch (err) {
      setImportError(err?.message || "HouseMind could not analyze those details.");
    } finally {
      setImportIntakeBusy(false);
    }
  }, [importIntakeDraft, refreshImports]);

  const handleUploadImportIntakeFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImportIntakeFileBusy(true);
    setImportNotice("");
    setImportError("");
    try {
      const result = await importCandidatesApi.discoverFile(file, {
        provider: importIntakeDraft.provider || "file_upload",
        source_account: importIntakeDraft.source_account || file.name,
      });
      const createdCount = Number(result?.created_count || 0);
      setImportNotice(createdCount > 0
        ? `HouseMind found ${createdCount} review item${createdCount === 1 ? "" : "s"} in ${file.name}.`
        : `HouseMind did not find reviewable household items in ${file.name}.`);
      await refreshImports();
    } catch (err) {
      setImportError(err?.message || "HouseMind could not analyze that file.");
    } finally {
      setImportIntakeFileBusy(false);
    }
  }, [importIntakeDraft.provider, importIntakeDraft.source_account, refreshImports]);

  const handleOpenImportedRecord = useCallback((route) => {
    if (!route) return;
    setImportDrawerOpen(false);
    navigateToApp(navigate, route);
  }, [navigate]);

  useEffect(() => {
    loadDashboardBootstrap();
    loadLocalWeather();
    loadTodayTasks();
    const deferSources = window.requestIdleCallback
      ? window.requestIdleCallback(() => loadImportSources(), { timeout: 2500 })
      : window.setTimeout(() => loadImportSources(), 1000);
    return () => window.requestIdleCallback ? window.cancelIdleCallback(deferSources) : window.clearTimeout(deferSources);
  }, [loadDashboardBootstrap, loadImportSources, loadLocalWeather, loadTodayTasks]);

  useEffect(() => {
    if (!importDrawerOpen) return undefined;
    loadTrustedImportRules();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [importDrawerOpen, loadTrustedImportRules]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      // Reuse the API layer's fresh bootstrap promise/cache. Explicit user
      // mutations still request a forced refresh, but merely returning to the
      // app should not restart every cross-module query.
      loadDashboardBootstrap();
      loadTodayTasks();
      loadLocalWeather();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadDashboardBootstrap, loadLocalWeather, loadTodayTasks]);

  const handleRefreshBriefing = useCallback(async () => {
    setRefreshingBriefing(true);
    setBriefingLoading(true);
    setBriefingOpen(true);
    try {
      await Promise.all([loadBriefing(true), loadLocalWeather()]);
    } catch {
    } finally {
      setRefreshingBriefing(false);
    }
  }, [loadBriefing, loadLocalWeather]);

  const collapseBriefing = useCallback((animate = false) => {
    setBriefingDismissedToday(true);
    if (animate && briefingOverlayMode) {
      setBriefingClosing(true);
      window.clearTimeout(briefingCloseTimerRef.current);
      briefingCloseTimerRef.current = window.setTimeout(() => {
        setBriefingOpen(false);
        setBriefingClosing(false);
        setBriefingSwipeOffset(0);
      }, 180);
      return;
    }
    setBriefingOpen(false);
    setBriefingClosing(false);
    setBriefingSwipeOffset(0);
  }, [briefingOverlayMode]);

  const toggleBriefingOpen = useCallback(() => {
    if (briefingOpen) {
      collapseBriefing(briefingOverlayMode);
      return;
    }
    setBriefingClosing(false);
    setBriefingSwipeOffset(0);
    setBriefingOpen(true);
  }, [briefingOpen, briefingOverlayMode, collapseBriefing]);

  useEffect(() => () => window.clearTimeout(briefingCloseTimerRef.current), []);

  const beginBriefingSwipeAt = useCallback((x, y) => {
    if (!briefingOverlayMode) return;
    briefingSwipeRef.current = { x, y };
  }, [briefingOverlayMode]);

  const moveBriefingSwipeTo = useCallback((x, y, preventDefault) => {
    const start = briefingSwipeRef.current;
    if (!start || !briefingOverlayMode) return;
    const deltaY = y - start.y;
    const deltaX = Math.abs(x - start.x);
    if (deltaY < 0 && Math.abs(deltaY) > deltaX) {
      preventDefault?.();
      setBriefingSwipeOffset(Math.max(-140, deltaY));
    }
  }, [briefingOverlayMode]);

  const endBriefingSwipeAt = useCallback((x, y) => {
    const start = briefingSwipeRef.current;
    briefingSwipeRef.current = null;
    if (!start || !briefingOverlayMode) return;
    const deltaY = y - start.y;
    const deltaX = Math.abs(x - start.x);
    if (deltaY <= -36 && Math.abs(deltaY) > deltaX * 0.9) {
      collapseBriefing(true);
      return;
    }
    setBriefingSwipeOffset(0);
  }, [briefingOverlayMode, collapseBriefing]);

  const beginBriefingSwipe = useCallback((event) => {
    if (event.pointerType === "touch") return;
    if (event.target.closest?.("button")) return;
    beginBriefingSwipeAt(event.clientX, event.clientY);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [beginBriefingSwipeAt]);

  const moveBriefingSwipe = useCallback((event) => {
    if (event.pointerType === "touch") return;
    moveBriefingSwipeTo(event.clientX, event.clientY, () => event.preventDefault());
  }, [moveBriefingSwipeTo]);

  const endBriefingSwipe = useCallback((event) => {
    if (event.pointerType === "touch") return;
    endBriefingSwipeAt(event.clientX, event.clientY);
  }, [endBriefingSwipeAt]);

  useEffect(() => {
    if (!briefingOpen || !briefingOverlayMode) return undefined;
    window.addEventListener("pointermove", moveBriefingSwipe, { passive: false });
    window.addEventListener("pointerup", endBriefingSwipe);
    window.addEventListener("pointercancel", endBriefingSwipe);
    return () => {
      window.removeEventListener("pointermove", moveBriefingSwipe);
      window.removeEventListener("pointerup", endBriefingSwipe);
      window.removeEventListener("pointercancel", endBriefingSwipe);
    };
  }, [briefingOpen, briefingOverlayMode, endBriefingSwipe, moveBriefingSwipe]);

  useEffect(() => {
    if (!briefingOpen || !briefingOverlayMode) return undefined;
    const swipeControl = briefingPanelRef.current?.querySelector(".hm-daily-brief-swipe");
    if (!swipeControl) return undefined;
    const startTouch = event => {
      const touch = event.touches?.[0];
      if (touch) beginBriefingSwipeAt(touch.clientX, touch.clientY);
    };
    const moveTouch = event => {
      const touch = event.touches?.[0];
      if (touch) moveBriefingSwipeTo(touch.clientX, touch.clientY, () => event.preventDefault());
    };
    const endTouch = event => {
      const touch = event.changedTouches?.[0];
      if (touch) endBriefingSwipeAt(touch.clientX, touch.clientY);
    };
    const cancelTouch = () => {
      briefingSwipeRef.current = null;
      setBriefingSwipeOffset(0);
    };
    swipeControl.addEventListener("touchstart", startTouch, { passive: true });
    swipeControl.addEventListener("touchmove", moveTouch, { passive: false });
    swipeControl.addEventListener("touchend", endTouch);
    swipeControl.addEventListener("touchcancel", cancelTouch);
    return () => {
      swipeControl.removeEventListener("touchstart", startTouch);
      swipeControl.removeEventListener("touchmove", moveTouch);
      swipeControl.removeEventListener("touchend", endTouch);
      swipeControl.removeEventListener("touchcancel", cancelTouch);
    };
  }, [briefingOpen, briefingOverlayMode, beginBriefingSwipeAt, endBriefingSwipeAt, moveBriefingSwipeTo]);

  const dismissKey = (item) => `${item.source_module}-${item.item_id}-${item.item_type}-${item.due_date || ""}`;

  const persistDismiss = useCallback(async (item, action) => {
    const key = dismissKey(item);
    setDismissed(prev => new Set([...prev, key]));
    setDismissTarget(null);
    try {
      await dashboardApi.dismissAttention(item, action);
    } catch {
      // Keep the local dismissal for this view even if persistence fails.
    }
  }, []);

  const handleDismiss = useCallback((item) => {
    setDismissTarget(item);
  }, []);

  const handleDoneDismiss = useCallback(async (item) => {
    await persistDismiss(item, "done");
  }, [persistDismiss]);

  const handleRemindDismiss = useCallback(async (item) => {
    await persistDismiss(item, "remind_tomorrow");
  }, [persistDismiss]);

  const openBulkAttention = useCallback((group) => {
    setBulkAttentionGroup(group);
    setBulkAttentionSelected(new Set(group.items.map(dismissKey)));
    setBulkAttentionError("");
  }, []);

  const applyBulkAttention = useCallback(async (action) => {
    if (!bulkAttentionGroup || !bulkAttentionSelected.size) return;
    const selectedItems = bulkAttentionGroup.items.filter(item => bulkAttentionSelected.has(dismissKey(item)));
    setBulkAttentionBusy(true);
    setBulkAttentionError("");
    try {
      await dashboardApi.dismissAttentionBulk(selectedItems, action);
      setDismissed(current => new Set([...current, ...selectedItems.map(dismissKey)]));
      setBulkAttentionGroup(null);
      setBulkAttentionSelected(new Set());
      window.dispatchEvent(new Event("hm_notifications_refresh"));
    } catch (error) {
      setBulkAttentionError(error?.message || "HouseMind could not update those items.");
    } finally {
      setBulkAttentionBusy(false);
    }
  }, [bulkAttentionGroup, bulkAttentionSelected]);

  const handleAddTodayTask = useCallback(async (event) => {
    event.preventDefault();
    const title = todayTaskDraft.trim();
    if (!title) return;
    setTodayTaskBusyId("add");
    try {
      const created = await dashboardApi.createTodayTask({ title });
      setTodayTasks(tasks => [created, ...tasks]);
      setTodayTaskDraft("");
      setTodayTasksOpen(true);
    } catch {
      // Leave the draft in place so the user can retry.
    } finally {
      setTodayTaskBusyId(null);
    }
  }, [todayTaskDraft]);

  const handleCompleteTodayTask = useCallback(async (task) => {
    setTodayTaskBusyId(task.id);
    try {
      const updated = await dashboardApi.updateTodayTask(task.id, { status: "completed" });
      setTodayTasks(tasks => tasks.map(item => item.id === task.id ? updated : item));
      setTodayTaskDecision(null);
    } catch {
    } finally {
      setTodayTaskBusyId(null);
    }
  }, []);

  const handleMoveTodayTaskTomorrow = useCallback(async (task) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dueDate = localISODate(tomorrow);
    setTodayTaskBusyId(task.id);
    try {
      await dashboardApi.updateTodayTask(task.id, { status: "open", due_date: dueDate });
      setTodayTasks(tasks => tasks.filter(item => item.id !== task.id));
      setTodayTaskDecision(null);
    } catch {
    } finally {
      setTodayTaskBusyId(null);
    }
  }, []);

  const handleDeleteTodayTask = useCallback(async (task) => {
    setTodayTaskBusyId(task.id);
    try {
      await dashboardApi.deleteTodayTask(task.id);
      setTodayTasks(tasks => tasks.filter(item => item.id !== task.id));
      setTodayTaskDecision(current => current?.id === task.id ? null : current);
    } catch {
    } finally {
      setTodayTaskBusyId(null);
    }
  }, []);

  const visibleItems = sortByPriority(attentionItems.filter(item =>
    !dismissed.has(dismissKey(item))
  ));
  const urgentItems   = visibleItems.filter(i => i.priority === "urgent");
  const soonItems     = visibleItems.filter(i => i.priority === "soon");
  const attentionQueueItems = visibleItems.filter(i => !["fyi", "upcoming", "savings"].includes(i.priority));
  const attentionSummary = attentionQueueItems.length > 0
    ? [
        urgentItems.length ? `${urgentItems.length} urgent` : null,
        soonItems.length ? `${soonItems.length} soon` : null,
      ].filter(Boolean).join(" · ")
    : "";
  const displayItems = showAllAlerts ? visibleItems : visibleItems.slice(0, 3);
  const attentionGroups = Object.values(visibleItems.reduce((groups, item) => {
    const key = `${item.source_module}:${item.item_type}`;
    if (!groups[key]) {
      const label = item.item_type === "pantry_expiry"
        ? "Pantry expiry"
        : item.item_type === "planned_meal_shortage"
          ? "Meal ingredients"
          : `${titleCase(item.source_module || "Household")} items`;
      groups[key] = { key, label, items: [] };
    }
    groups[key].items.push(item);
    return groups;
  }, {})).filter(group => group.items.length > 1);
  const dailySnapshot = briefing?.daily_snapshot || {};
  const dailySnapshotItems = Array.isArray(dailySnapshot.items) ? dailySnapshot.items : [];
  const todayMeals = Array.isArray(briefing?.today_meals) ? briefing.today_meals : [];
  const mealItems = todayMeals.map((meal, index) => ({
    id: `meal-${meal.meal_type || index}`,
    icon: meal.meal_type === "breakfast" ? "🥣" : meal.meal_type === "lunch" ? "🥗" : "🍽",
    eyebrow: `${titleCase(meal.meal_type || "Meal")} today`,
    title: meal.display_meal_name || meal.meal_name || "Meal planned",
    detail: meal.prep_note || meal.notes || "",
    path: "/app/food",
    action_label: "View meals",
  }));
  const urgentBriefItems = urgentItems
    .filter(item =>
      item.item_type !== "personal_task_due"
      && item.item_type !== "occasion_upcoming"
      && (item.item_type !== "pantry_expiry" || (daysUntilLocalDate(item.due_date) ?? Number.POSITIVE_INFINITY) <= 0)
    )
    .map(item => ({ ...item, id: `urgent-${item.source_module}-${item.item_id}`, detail: attentionDescription(item), dismissible: true }));
  const focusAttentionItems = visibleItems
    .filter(item => ["personal_task_due", "occasion_upcoming"].includes(item.item_type) && (daysUntilLocalDate(item.due_date) ?? 0) <= 1)
    .map(item => ({ ...item, id: `focus-${item.source_module}-${item.item_id}`, eyebrow: item.item_type === "personal_task_due" ? "Personal task" : "Upcoming occasion", detail: attentionDescription(item), dismissible: true }));
  const snapshotFocusItems = dailySnapshotItems
    .filter(item => !String(item.id || "").startsWith("attention:"))
    .map(item => ({ ...item, icon: item.kind === "trip" ? "✈" : item.kind === "pet" ? "🐾" : "📅" }));
  const focusBriefItems = [...focusAttentionItems, ...snapshotFocusItems, ...mealItems]
    .filter((item, index, rows) => rows.findIndex(candidate => candidate.id === item.id) === index);
  const morningActionCount = urgentBriefItems.length + focusBriefItems.length;
  const dailySnapshotLabel = briefingLoading
    ? "Loading today…"
    : morningActionCount
      ? `${morningActionCount} for today`
      : "All calm";
  const ownerFirstName = (user?.full_name || user?.workspace?.first_name || "there").trim().split(/\s+/)[0];
  const currentHour = today.getHours();
  const greetingPeriod = currentHour < 12 ? "Good morning" : currentHour < 17 ? "Good afternoon" : "Good evening";
  const welcomeLead = currentHour < 12
    ? "How was your night? I hope you woke up feeling rested."
    : currentHour < 17
      ? "I hope your morning has gone well."
      : "I hope your day has gone well.";
  const weatherTemperature = Number(localWeather?.temperature_f);
  const weatherFeelsLike = Number(localWeather?.feels_like_f);
  const weatherLocation = String(localWeather?.location || "your area").split(",")[0];
  const weatherHigh = Number(localWeather?.high_f);
  const weatherLow = Number(localWeather?.low_f);
  const weatherSentence = localWeather && Number.isFinite(weatherTemperature)
    ? ` Today in ${weatherLocation}, expect ${weatherConditionLabel(localWeather.weather_code)} conditions${Number.isFinite(weatherHigh) && Number.isFinite(weatherLow) ? ` with a high of ${Math.round(weatherHigh)}°F and a low of ${Math.round(weatherLow)}°F` : `; it’s currently ${Math.round(weatherTemperature)}°F${Number.isFinite(weatherFeelsLike) && Math.abs(weatherFeelsLike - weatherTemperature) >= 2 ? ` and feels like ${Math.round(weatherFeelsLike)}°F` : ""}`}.`
    : "";
  const morningWelcome = `${welcomeLead}${weatherSentence} Here’s what HouseMind has lined up for today.`;

  useEffect(() => {
    const media = window.matchMedia(DAILY_BRIEF_OVERLAY_QUERY);
    const syncMode = () => setBriefingOverlayMode(media.matches);
    syncMode();
    media.addEventListener?.("change", syncMode);
    return () => media.removeEventListener?.("change", syncMode);
  }, []);

  useEffect(() => {
    if (!briefingOpen || !briefingOverlayMode) return undefined;
    document.body.classList.add("hm-daily-brief-overlay-open");
    const focusFrame = window.requestAnimationFrame(() => briefingPanelRef.current?.focus());
    const closeOnEscape = event => {
      if (event.key !== "Escape") return;
      collapseBriefing(true);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("hm-daily-brief-overlay-open");
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [briefingOpen, briefingOverlayMode, collapseBriefing]);

  useEffect(() => {
    const refreshCalendarDay = () => setBriefingDay(localISODate(new Date()));
    window.addEventListener("focus", refreshCalendarDay);
    document.addEventListener("visibilitychange", refreshCalendarDay);
    return () => {
      window.removeEventListener("focus", refreshCalendarDay);
      document.removeEventListener("visibilitychange", refreshCalendarDay);
    };
  }, []);

  useEffect(() => {
    if (dailyBriefRequested) {
      setBriefingOpen(true);
      return;
    }
    setBriefingOpen(currentHour < 12 && !wasBriefingDismissedToday());
  }, [briefingDay, currentHour, dailyBriefRequested]);

  useEffect(() => {
    if (!briefingOpen || briefingOverlayMode) return undefined;
    const collapseAfterHero = () => {
      if ((heroRef.current?.getBoundingClientRect().bottom || 1) > 0) return;
      setBriefingDismissedToday(true);
      setBriefingOpen(false);
    };
    window.addEventListener("scroll", collapseAfterHero, { passive: true });
    return () => window.removeEventListener("scroll", collapseAfterHero);
  }, [briefingOpen, briefingOverlayMode]);

  const dailyBriefPanel = briefingOpen ? (
    <div
      id="hm-daily-brief-panel"
      className={`hm-hero-panel hm-daily-brief-panel${briefingClosing ? " is-swipe-closing" : ""}`}
      ref={briefingPanelRef}
      role="region"
      aria-label="Daily Brief"
      tabIndex={-1}
      style={{ "--hm-daily-brief-swipe-y": `${briefingSwipeOffset}px` }}
    >
      <div className="hm-daily-brief-chrome">
        <div className="hm-hero-panel-header" onClick={() => collapseBriefing(briefingOverlayMode)}>
          <span>Daily briefing</span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              onClick={e => { e.stopPropagation(); handleRefreshBriefing(); }}
              disabled={refreshingBriefing}
              title="Regenerate briefing"
              aria-label="Regenerate Daily Brief"
              style={{
                background: "transparent",
                border: "none",
                cursor: refreshingBriefing ? "default" : "pointer",
                color: "var(--hm-muted)",
                fontSize: 13,
                padding: "2px 4px",
                opacity: refreshingBriefing ? 0.5 : 1,
              }}
            >
              {refreshingBriefing ? "…" : "↻"}
            </button>
            <button
              type="button"
              className="hm-daily-brief-collapse"
              onClick={e => { e.stopPropagation(); collapseBriefing(briefingOverlayMode); }}
              aria-label="Collapse Daily Brief"
            >
              <span
                className="hm-brief-chevron"
                style={{ transform: "rotate(180deg)" }}
                aria-hidden="true"
              >▾</span>
            </button>
          </div>
        </div>
      </div>

      {briefingLoading ? (
        <div className="hm-brief-row" style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--hm-muted)" }}>
          <span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} />
          Generating your morning briefing…
        </div>
      ) : (
        <div className="hm-brief-row hm-brief-row--structured">
          <section className="hm-morning-welcome">
            <div className="hm-morning-welcome__head">
              <span aria-hidden="true">🌅</span>
              <strong>{greetingPeriod}, {ownerFirstName}</strong>
              {localWeather && Number.isFinite(weatherTemperature) && <em>{Math.round(weatherTemperature)}° · {weatherConditionLabel(localWeather.weather_code)}</em>}
            </div>
            <p>{morningWelcome}</p>
          </section>
          <div className="hm-morning-headline">
            <div><span aria-hidden="true">🌅</span><div><strong>Morning Brief</strong><small>{dateLabel}</small></div></div>
            <b>{morningActionCount ? `${morningActionCount} action${morningActionCount === 1 ? "" : "s"}` : "All calm"}</b>
          </div>
          <MorningBriefSection icon="🚨" title="Urgent" items={urgentBriefItems} emptyText="No urgent household issues." />
          <MorningBriefSection icon="☀️" title="Today’s focus" items={focusBriefItems} emptyText="Nothing scheduled for today." />
          {!morningActionCount && focusBriefItems.length === 0 && <div className="hm-morning-clear"><span>✓</span><div><strong>Your household is ready for today</strong><small>No immediate action is needed.</small></div></div>}
        </div>
      )}
      <div
        className="hm-daily-brief-swipe"
        title="Swipe up or tap to collapse Daily Brief"
        role="button"
        tabIndex={0}
        aria-label="Swipe up to collapse Daily Brief"
        onPointerDown={beginBriefingSwipe}
        onPointerCancel={() => { briefingSwipeRef.current = null; setBriefingSwipeOffset(0); }}
        onClick={() => collapseBriefing(briefingOverlayMode)}
        onKeyDown={event => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          collapseBriefing(briefingOverlayMode);
        }}
      >
        <span aria-hidden="true" />
      </div>
    </div>
  ) : null;

  return (
    <>
      <AttentionDismissModal
        item={dismissTarget}
        onClose={() => setDismissTarget(null)}
        onDone={handleDoneDismiss}
        onRemind={handleRemindDismiss}
      />
      <BulkAttentionModal
        group={bulkAttentionGroup}
        selectedKeys={bulkAttentionSelected}
        busy={bulkAttentionBusy}
        error={bulkAttentionError}
        onToggle={key => setBulkAttentionSelected(current => {
          const next = new Set(current);
          if (next.has(key)) next.delete(key); else next.add(key);
          return next;
        })}
        onToggleAll={() => setBulkAttentionSelected(current => (
          current.size === bulkAttentionGroup?.items.length
            ? new Set()
            : new Set((bulkAttentionGroup?.items || []).map(dismissKey))
        ))}
        onClose={() => !bulkAttentionBusy && setBulkAttentionGroup(null)}
        onApply={applyBulkAttention}
      />
      <FoundByHouseMindDrawer
        open={importDrawerOpen}
        candidates={importCandidates}
        loading={importLoading}
        busyId={importBusyId}
        notice={importNotice}
        error={importError}
        intakeOpen={importIntakeOpen}
        intakeDraft={importIntakeDraft}
        intakeBusy={importIntakeBusy}
        intakeFileBusy={importIntakeFileBusy}
        historyOpen={importHistoryOpen}
        history={importHistory}
        historyLoading={importHistoryLoading}
        historyFilters={importHistoryFilters}
        historyMeta={importHistoryMeta}
        sourcesOpen={importSourcesOpen}
        sourcesPayload={importSourcesPayload}
        sourcesLoading={importSourcesLoading}
        sourceBusyId={importSourceBusyId}
        trustedRules={trustedImportRules}
        trustedRulesLoading={trustedImportRulesLoading}
        onRefresh={refreshImports}
        onApprove={handleApproveImport}
        onApproveAndTrust={handleApproveAndTrustImport}
        onMerge={handleMergeImport}
        onMergeAndTrust={handleMergeAndTrustImport}
        onResolveMerge={handleResolveMergeImport}
        onCorrect={handleCorrectImport}
        onDismiss={handleDismissImport}
        onApproveHighConfidence={handleApproveHighConfidence}
        onToggleIntake={() => setImportIntakeOpen(open => !open)}
        onToggleHistory={toggleImportHistory}
        onToggleSources={toggleImportSources}
        onSourceAction={handleImportSourceAction}
        onDisableTrustedRule={handleDisableTrustedImportRule}
        onHistoryFilterChange={handleImportHistoryFilterChange}
        onDeleteHistory={handleDeleteImportHistory}
        onIntakeDraftChange={updateImportIntakeDraft}
        onSubmitIntake={handleSubmitImportIntake}
        onUploadIntakeFile={handleUploadImportIntakeFile}
        onOpenRecord={handleOpenImportedRecord}
        focusedCandidateId={focusedImportId}
        onClose={closeImportDrawer}
      />
      {importConfirmation && (
        <ConfirmModal
          tag={importConfirmation.tag}
          title={importConfirmation.title}
          message={importConfirmation.message}
          confirmLabel={importConfirmation.confirmLabel}
          cancelLabel={importConfirmation.cancelLabel}
          danger={importConfirmation.danger}
          loading={Boolean(importConfirmation.loading)}
          onCancel={() => setImportConfirmation(null)}
          onConfirm={async () => {
            const action = importConfirmation.onConfirm;
            setImportConfirmation(current => current ? { ...current, loading: true } : current);
            try {
              await action();
            } finally {
              setImportConfirmation(null);
            }
          }}
        />
      )}
      {briefingOpen && briefingOverlayMode && createPortal(dailyBriefPanel, document.body)}
      <div className="dashboard-page-body">
        {/* ── Zone 1: Hero with AI Briefing ─────────────────────────────── */}
        <header className="hm-hero" ref={heroRef}>
          <div className="hm-hero-copy">
            <p>Your household, handled</p>
            <h1>
              {visibleItems.length > 0
                ? `${visibleItems.length} item${visibleItems.length !== 1 ? "s" : ""} need your attention.`
                : "Everything looks calm today."}
            </h1>
            <span>
              {dateLabel}
              {attentionSummary && ` · ${attentionSummary}`}
              {visibleItems.length > 0 && ` · ${visibleItems.length} active alert${visibleItems.length !== 1 ? "s" : ""} across your household`}
            </span>
          </div>

          {briefingOpen ? (!briefingOverlayMode && dailyBriefPanel) : <div className="hm-brief-collapsed">
            <button
              type="button"
              className="hm-daily-brief-pill"
              onClick={toggleBriefingOpen}
              aria-expanded={false}
              aria-controls="hm-daily-brief-panel"
            >
              <span aria-hidden="true">✦</span>
              <strong>Daily Brief</strong>
              <small>{dailySnapshotLabel}</small>
              <b>Open</b>
            </button>
          </div>}
        </header>

        {connectionAlerts.length > 0 && (
          <section className="hm-connection-alert" role="status">
            <span className="hm-connection-alert__icon" aria-hidden="true">!</span>
            <div>
              <p>Discovery connection{connectionAlerts.length === 1 ? "" : "s"} need attention</p>
              <strong>{connectionAlerts[0].source.label.replace(" connector", "")}</strong>
              <small>{connectionAlerts[0].reason}{connectionAlerts.length > 1 ? ` · +${connectionAlerts.length - 1} more` : ""}</small>
            </div>
            <button type="button" onClick={() => navigate(`/app/connections?source=${connectionAlerts[0].source.id}`)}>Fix connection</button>
          </section>
        )}

        <HouseholdActivityBar
          discoveryCount={importCount}
          discoveryLoading={importCountLoading}
          cases={openCases}
          caseCount={caseCount}
          casesLoading={casesLoading}
          graph={householdGraph}
          graphLoading={householdGraphLoading}
          onDiscovery={openImportDrawer}
          onCases={() => {
            if (householdCasesOpen) {
              setHouseholdCasesOpen(false);
            } else {
              setHouseholdCasesOpen(true);
              if (!openCases.length) loadCases();
            }
          }}
          onGraph={() => householdGraphOpen ? setHouseholdGraphOpen(false) : openHouseholdGraph()}
          casesOpen={householdCasesOpen}
          graphOpen={householdGraphOpen}
        />

        <HouseholdCases cases={openCases} summary={caseSummary} loading={casesLoading} selected={selectedCase} onSelect={setSelectedCase} onClose={closeSelectedCase} onStatus={updateCaseStatus} hidePanel={!householdCasesOpen} />

        {householdGraphOpen && <HouseholdGraphSnapshot graph={householdGraph} loading={householdGraphLoading} onClose={() => setHouseholdGraphOpen(false)} onRefresh={loadHouseholdGraph} />}

        <HealthScoreCard />

        <PredictionsPanel />

        {/* Morning Brief summarizes the day; this queue remains available all day. */}
        {!attentionLoading && (
          <section id="dashboard-attention" style={{ marginTop: 24 }}>
            <div className="hm-attention-section-head">
              <div>
                <p>🚨 Action needed</p>
                <h2>⚠️ Attention items</h2>
              </div>
              {visibleItems.length > 3 && (
                <button type="button" onClick={() => setShowAllAlerts(value => !value)}>
                  {showAllAlerts ? "Show fewer ▲" : `View ${visibleItems.length - 3} more ▼`}
                </button>
              )}
            </div>
            <div className="hm-action-needed-stack">
              {attentionGroups.length > 0 && (
                <div className="hm-attention-group-actions" aria-label="Manage similar attention items">
                  <span>Manage similar items</span>
                  <div>
                    {attentionGroups.map(group => (
                      <button type="button" key={group.key} onClick={() => openBulkAttention(group)}>
                        {group.label} <b>{group.items.length}</b>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {visibleItems.length > 0 ? (
                <div className="hm-attention-card-list">
                  {displayItems.map((item, index) => (
                    <AttentionCard
                      key={`${item.source_module}-${item.item_id}-${item.item_type}-${index}`}
                      item={item}
                      onDismiss={handleDismiss}
                    />
                  ))}
                </div>
              ) : (
                <div className="hm-attention-clear">
                  <span>✓</span>
                  <div>
                    <strong>All clear across your household</strong>
                    <p>No urgent items, expiring warranties, overdue services, or upcoming renewals.</p>
                  </div>
                </div>
              )}
              {!todayTasksLoading && (
                <TodayTasksPanel
                  tasks={todayTasks}
                  draft={todayTaskDraft}
                  onDraftChange={setTodayTaskDraft}
                  onAdd={handleAddTodayTask}
                  onComplete={handleCompleteTodayTask}
                  onMoveTomorrow={handleMoveTodayTaskTomorrow}
                  onDelete={handleDeleteTodayTask}
                  busyId={todayTaskBusyId}
                  showAll={showAllTodayTasks}
                  onToggleShowAll={() => setShowAllTodayTasks(v => !v)}
                  open={todayTasksOpen}
                  onToggleOpen={() => setTodayTasksOpen(v => !v)}
                  decisionTask={todayTaskDecision}
                  onDecisionTaskChange={setTodayTaskDecision}
                />
              )}
            </div>
          </section>
        )}

        {attentionLoading && (
          <section style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 10, color: "var(--hm-muted)", fontSize: 14 }}>
            <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
            Scanning your household for alerts…
          </section>
        )}

        {/* ── Zone 2b: Cross-module Insights ──────────────────────────────── */}
        <InsightsPanel focusRequested={focusIntelligence} />

        {/* ── Zone 3: Widget Grid ────────────────────────────────────────── */}
        <section style={{ marginTop: 28 }}>
          <div style={{ marginBottom: 16 }}>
            <p style={{
              margin: "0 0 2px",
              color: "var(--hm-cyan)",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}>
              🏠 Your household
            </p>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--hm-text)" }}>
              📊 Overview
            </h2>
          </div>

          <div className="hm-dashboard-widget-grid">
            <TimelineWidget />
            <FoodWidget />
            <VaultWidget />
            <HomeCareWidget />
            <PropertyProfileWidget />
            <SafetyWidget />
            <VehiclesWidget />
            <OccasionsWidget />
            <EventsWidget />
            <PetsWidget />
            <BudgetWidget />
            <UtilitiesWidget />
            <SubSpendWidget />
          </div>
        </section>
      </div>

      {/* Global Ask FAB */}
      <button
        type="button"
        className="hm-ask-fab"
        onClick={() => window.dispatchEvent(new CustomEvent("hm_open_ask", { detail: {} }))}
        aria-label="Ask HouseMind"
      >
        <span className="hm-ask-fab__star" aria-hidden="true">✶</span>
        <span className="hm-ask-fab__label"><span>Ask</span><span>HouseMind</span></span>
      </button>
    </>
  );
}
